import {
  ADORNMENT_MARGIN,
  CARD_LAYOUT_SPACING,
  COMANAGER_HEIGHT,
  enumAlignStyle,
  enumBoxHeightMode,
  enumBranchStyle,
  enumChartTypes,
  enumContainerType,
  enumSubChartFlag,
  needToRecenterToPage,
  ONE_COLUMN_OFFSET,
  SHADOW_OFFSET,
  SNAP_CENTER_DISTANCE,
  WALL_THICKNESS
} from "../constants";
import { GetFieldDefBy } from "../utils/GetFieldDefBy";
import { arraySum } from "../utils/arraySum";
import { IsPureVerticalStyle } from "../utils/IsPureVerticalStyle";
import { NodePoly } from "./NodePoly";
import { QRect } from "./QRect";
import { onlyUnique } from "../utils/onlyUnique";

export class NodeEntity {
  constructor() {
    this.fID = -1;
    this.fParentNodeEntity = null;
    this.fNode = null;
    this.fEntityType = enumContainerType.nonmrb;
    this.fBox = null;
    this.fCalculatedPosition = { x: 0, y: 0 };
    this.fCorrectedChartHeight = 0;
    this.fShadow = false;
    this.fAnchorDown = null;
    this.fAnchorUp = null;
    this.fMultiColumnJoints = null;
    this.fNodePoly = null;
    this.fBasePoly = null;
    this.fDescendents = null;
    this.fBranchStyle = null;
    this.fSubBranchStyle = null;
    this.fHasAuxline = false;
    this.fBranchMgr = null;
  }

  InitNodeEntity(aNode, parentNodeEntity) {
    if (parentNodeEntity && parentNodeEntity.fBranchMgr)
      this.fBranchMgr = parentNodeEntity.fBranchMgr;

    var rootContainerNode = null;
    var isPositionNode = aNode.IsPositionsNode();

    if (isPositionNode) rootContainerNode = aNode;
    else rootContainerNode = aNode.GetContainerNode();

    var isContainer = rootContainerNode !== null && parentNodeEntity === null;
    if (isContainer) {
      // this is a root container
      rootContainerNode.SetNodeEntity(this);
      this.fID = rootContainerNode.GetID();
      this.fShadow = rootContainerNode.HasShadow();
      this.fNode = rootContainerNode;
      this.UpdateBranchStyle();
    } else {
      aNode.SetNodeEntity(this);
      this.fID = aNode.GetDataObjID();
      this.fShadow = aNode.HasShadow();
      this.fNode = aNode;
      this.UpdateBranchStyle();
    }
    this.fParentNodeEntity = parentNodeEntity;
    this.fHasAuxline = false;
    if (this.fDescendents) {
      for (var key in this.fDescendents) {
        delete this.fDescendents[key];
      }
    }
    this.fMultiColumnJoints = [];

    if (isContainer || isPositionNode) {
      this.fEntityType = rootContainerNode.GetContainerType();
      var associatedNodes = rootContainerNode.GetAssociatedNodes();

      for (var i = 0; i < associatedNodes.length; i++) {
        var associatedNode = associatedNodes[i];
        var associatedEntity = new NodeEntity();
        associatedEntity.InitNodeEntity(associatedNode, this);
        this.AddEntity("InsideChild", associatedEntity);
        this.GrabChildren(associatedEntity);
      }

      this.ReOrderContainerChildren();
      var firstChild = this.GetEntity("Subordinate", 0);
      if (firstChild) {
        // Prefer fSubBranchStyle for containers; fBranchStyle is their connection style.
        this.SetSubBranchStyle(firstChild.fSubBranchStyle ?? firstChild.fBranchStyle);
      } else {
        this.SetSubBranchStyle(enumBranchStyle.tree);
      }

      if (isPositionNode) {
        this.HandleChildEntities(aNode);
      }
    } else {
      this.HandleChildEntities(aNode);
    }
  }

  HandleChildEntities(aNode) {
    var childrenNodes = [];
    var numChildren = aNode.GetNumChildren();
    for (var i = 0; i < numChildren; i++) {
      childrenNodes.push(aNode.GetIndexChild(i));
    }
    var idx = 0;
    var isFirst = true;
    while (idx < childrenNodes.length) {
      var childNode = childrenNodes[idx];
      if (!childNode) {
        idx++;
        isFirst = false;
        continue;
      }
      var containerNode = childNode.GetContainerNode();
      if (containerNode !== null && !containerNode.IsPositionsNode()) {
        // this is a non position container node and needs to be created similarly
        this.AddContainerChildWithContent(childrenNodes, idx);
      } else {
        this.AddChildEntity(childNode);
        idx++;
      }
      if (isFirst)
        // mimic 'firstChild' branch style setting from above.
        this.SetSubBranchStyle(childNode.GetLayout().GetBranchStyle());
      isFirst = false;
    }
  }

  InitFlatEntity(aNode, parentNodeEntity) {
    if (aNode.GetContainerType() === enumContainerType.standardmrb) {
      var rootContainerNode = aNode;
    } else {
      rootContainerNode = aNode.GetContainerNode();
    }

    if (
      this.fBranchMgr.GetChartType() !== enumChartTypes.card &&
      (rootContainerNode === null || parentNodeEntity !== null)
    ) {
      // The assumptions and conditions for index page is not met.
      console.log("Illegal index page content");
      return;
    }

    function SetupEntity(node, entity, entityParent) {
      // if node is null, it must be special case of card type. Create an empty shell that contains all nodes
      // This empty shell does not have a page node associated with it.
      if (node) {
        node.SetNodeEntity(entity);
        entity.fID = node.GetID();
        entity.fShadow = node.HasShadow();
        entity.fEntityType = node.GetContainerType();
      } else {
        entity.fID = -1;
        entity.fShadow = false;
        entity.fEntityType = enumContainerType.standardmrb;
      }
      entity.fNode = node;
      entity.fParentNodeEntity = entityParent;
      if (entityParent != null) {
        entity.fBranchMgr = entityParent.fBranchMgr;
      }
      entity.UpdateBranchStyle();
      entity.InitBaseGeometry(0);
      entity.ConstructPoly();
      entity.fMultiColumnJoints = [];
    }

    SetupEntity(rootContainerNode, this, parentNodeEntity);
    var rowOrder = false;
    if (rootContainerNode) {
      var insideNodes = rootContainerNode.GetAssociatedNodes();
      var numColumns = rootContainerNode.GetContainerNumCols();
    } else {
      insideNodes = aNode.GetAllBranchNodes();
      rowOrder = true;
    }

    if (rowOrder) {
      for (var i = 0; i < insideNodes.length; i++) {
        var insideNode = insideNodes[i];
        var insideEntity = new NodeEntity();
        SetupEntity(insideNode, insideEntity, this);
        this.AddEntity("InsideChild", insideEntity);
      }
    } // to make entities sorted by column
    else {
      var n = insideNodes.length;
      var colOffsets = [];
      var totalOffsets = 0;
      for (i = 0; i < numColumns - 1; i++) {
        var numRows = Math.ceil(n / (numColumns - i));
        colOffsets.push(numRows);
        n -= numRows;
        totalOffsets += numRows;
      }
      colOffsets.push(1 - totalOffsets);

      var ix = 0;
      var ic = 0;
      for (i = 0; i < insideNodes.length; i++) {
        insideNode = insideNodes[ix];
        insideEntity = new NodeEntity();
        SetupEntity(insideNode, insideEntity, this);
        this.AddEntity("InsideChild", insideEntity);
        ix += colOffsets[ic++];
        if (ic === colOffsets.length) {
          ic = 0;
        }
      }
    }
  }

  // This is where a node entity and all its descendents are initialized. The result is a shadow orgchart
  // that layout calculation is done.
  // 1- Initialization of the org tree
  // 2- Initialization of geometry
  //    Looks like for adjustable width, we need to separate the two.
  Initialize(aNode, parentNodeEntity, branchMgr) {
    this.fBranchMgr = branchMgr;
    if (
      aNode.GetBranchStyle() === enumBranchStyle.index ||
      branchMgr.GetChartType() === enumChartTypes.card
    ) {
      //ignore
      this.InitFlatEntity(aNode, parentNodeEntity);
      return;
    } else this.InitNodeEntity(aNode, parentNodeEntity);

    function InitNodeGeometry(entity) {
      entity.InitBaseGeometry(0);
      var children = entity.GetChildren();
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        InitNodeGeometry(child);
      }

      entity.UpdateForStaggeredStyle();
      entity.UpdateForLevelChart();
    }

    function ConstructAllPolys(entity) {
      entity.ConstructPoly();
      var children = entity.GetChildren();
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        ConstructAllPolys(child);
      }
    }

    InitNodeGeometry(this);
    ConstructAllPolys(this);
    //this.fNodePoly.logPointsOnly('');
  }

  UpdateForLevelChart() {
    var chartType = this.fBranchMgr.GetChartType();
    if (chartType === enumChartTypes.level) {
      this.SetSubBranchStyle(enumBranchStyle.tree);
      var subs = this.GetEntities("Subordinate");
      this.AdjustSpacingsForLeveling(subs);
    }
  }

  UpdateForStaggeredStyle() {
    var subs = this.GetEntities("Subordinate");
    if (this.fSubBranchStyle === enumBranchStyle.stagger && subs.length >= 2) {
      var subsEven = [];
      var subsOdd = [];
      for (var i = 0; i < subs.length; i = i + 2) {
        subsEven.push(subs[i]);
      }
      for (i = 1; i < subs.length; i += 2) {
        subsOdd.push(subs[i]);
      }

      // find maximum height that odd number boxes are going to be pushed down
      var maxHeightEvens = GetMaxHeight(subsEven) + this.GetSubChartBox(subsEven, "bottom").height;
      var maxHeightOdds = GetMaxHeight(subsOdd) + this.GetSubChartBox(subsOdd, "bottom").height;

      // updating odd subs
      for (i = 0; i < subsOdd.length; i++) {
        var sub = subsOdd[i];
        var myHeightDiff =
          maxHeightOdds - (sub.fBox.fHeight + sub.fBox.fMargins.fTop + sub.fBox.fMargins.fBottom);
        sub.fBox.fSpacing.fTop = sub.fBox.fSpacing.fTop + maxHeightEvens + sub.fBox.fMargins.fTop;
        sub.fBox.fSpacing.fBottom =
          sub.fBox.fSpacing.fBottom + myHeightDiff + sub.fBox.fMargins.fBottom;
      }

      // updating even subs
      for (i = 0; i < subsEven.length; i++) {
        sub = subsEven[i];
        myHeightDiff =
          maxHeightOdds - (sub.fBox.fHeight + sub.fBox.fMargins.fTop + sub.fBox.fMargins.fBottom);
        sub.fBox.fSpacing.fBottom =
          sub.fBox.fSpacing.fBottom + maxHeightEvens + myHeightDiff + sub.fBox.fMargins.fBottom;
      }
    }

    /**
     * @return {number}
     */
    function GetMaxHeight(entities) {
      var maxHeight = 0;
      for (var i = 0; i < entities.length; i++) {
        var entity = entities[i];
        maxHeight = Math.max(
          maxHeight,
          entity.fBox.fHeight +
            entity.fBox.fMargins.fTop +
            entity.fBox.fMargins.fBottom +
            (entity.fShadow ? SHADOW_OFFSET : 0)
        );
      }
      return maxHeight;
    }
  }

  UpdateBranchStyle(branchStyle) {
    if (!this.fNode) {
      this.fBranchStyle = enumBranchStyle.tree;
    } else {
      if (typeof branchStyle === "undefined") {
        branchStyle = this.fNode.GetBranchStyle(); // have seen problems here with container layout's branch style being tree, which differs from its inside child
      }
      if (!this.fBranchMgr) console.log(":(");
      else if (
        this.fBranchMgr.GetChartType() === enumChartTypes.level &&
        branchStyle !== enumBranchStyle.index
      ) {
        this.fBranchStyle = enumBranchStyle.tree;
      } else {
        this.fBranchStyle = branchStyle;
      }
    }
    //console.log('NodeID: ' + this.fNode.fDataObjID + ', Branch Style: ' + branchStyle);
  }

  // it is determined that first siblingNode is part of a container. Find all associated nodes
  // from siblingNodes and add them to a newly created container entity. siblingNodes will shrink to remain
  // only non associated nodes.
  // newly created container node is added to list of subordinates of 'this'
  AddContainerChildWithContent(siblingNodes, startIdx) {
    startIdx = startIdx || 0;
    if (startIdx >= siblingNodes.length) {
      return;
    }

    var firstNode = siblingNodes[startIdx];
    var containerNode = firstNode.GetContainerNode();
    if (containerNode === null) {
      return;
    }

    var containerType = containerNode.GetContainerType();
    // first create an entity that is the container
    var containerEntity = new NodeEntity();
    containerEntity.fEntityType = containerType;
    containerEntity.InitNodeEntity(containerNode, this);

    // Build Set of associated object IDs for O(1) lookup
    var associatedNodes = containerNode.GetAssociatedNodes();
    var associatedIDs = new Set();
    for (var i = 0; i < associatedNodes.length; i++) {
      associatedIDs.add(associatedNodes[i].GetObjectID());
    }

    // Single pass: find and process all associated nodes, collect non-associated
    var kept = [];
    for (i = startIdx; i < siblingNodes.length; i++) {
      var node = siblingNodes[i];
      if (associatedIDs.has(node.GetObjectID())) {
        var insideChildEntity = new NodeEntity();
        insideChildEntity.InitNodeEntity(node, containerEntity);
        containerEntity.AddEntity("InsideChild", insideChildEntity);
        containerEntity.SetBranchStyle(insideChildEntity.fBranchStyle);
        containerEntity.GrabChildren(insideChildEntity);
      } else {
        kept.push(node);
      }
    }
    // Replace siblingNodes from startIdx onward in-place
    siblingNodes.length = startIdx;
    for (i = 0; i < kept.length; i++) {
      siblingNodes.push(kept[i]);
    }

    // re-order all outside children if an order list exists.
    containerEntity.ReOrderContainerChildren();

    // make branch style to be of first child if it exists, otherwise, tree style
    var firstChild = containerEntity.GetEntity("Subordinate", 0);
    if (firstChild) {
      // Prefer fSubBranchStyle for containers; fBranchStyle is their connection style.
      containerEntity.SetSubBranchStyle(firstChild.fSubBranchStyle ?? firstChild.fBranchStyle);
    } else {
      containerEntity.SetSubBranchStyle(enumBranchStyle.tree);
    }

    switch (containerEntity.fBranchStyle) {
      case enumBranchStyle.assistantpeerleft:
        this.AddEntity("PeerLeft", containerEntity);
        break;

      case enumBranchStyle.assistantright:
        this.AddEntity("AssistantRight", containerEntity);
        break;
      case enumBranchStyle.assistantleft:
        this.AddEntity("AssistantLeft", containerEntity);
        break;

      case enumBranchStyle.assistantpeerright:
        this.AddEntity("PeerRight", containerEntity);
        break;

      default:
        this.AddEntity("Subordinate", containerEntity);
    }
  }

  AddChildEntity(childNode) {
    // this is a normal node, (subordinate, assistant, etc.)
    var childEntity = new NodeEntity();
    childEntity.InitNodeEntity(childNode, this);
    var childBranchStyle = childEntity.fBranchStyle;
    switch (childBranchStyle) {
      case enumBranchStyle.twocolumn:
      case enumBranchStyle.fourcolumn:
      case enumBranchStyle.sixcolumn:
      case enumBranchStyle.eightcolumn:
      case enumBranchStyle.twelvecolumn:
      case enumBranchStyle.sixteencolumn:
      case enumBranchStyle.twentycolumn:
      case enumBranchStyle.tree:
      case enumBranchStyle.stagger:
      case enumBranchStyle.onecolumncenterright:
      case enumBranchStyle.onecolumncenterleft:
      case enumBranchStyle.onecolumnright:
      case enumBranchStyle.onecolumnleft:
      case enumBranchStyle.banded:
        this.AddEntity("Subordinate", childEntity);
        this.fSubBranchStyle = childBranchStyle;
        break;

      case enumBranchStyle.assistantpeerleft:
        this.AddEntity("PeerLeft", childEntity);
        break;

      case enumBranchStyle.assistantright:
        this.AddEntity("AssistantRight", childEntity);
        break;
      case enumBranchStyle.assistantleft:
        this.AddEntity("AssistantLeft", childEntity);
        break;

      case enumBranchStyle.assistantpeerright:
        this.AddEntity("PeerRight", childEntity);
        break;
    }
  }

  AddEntity(key, entityNode) {
    if (!this.fDescendents) {
      this.fDescendents = new Object();
    }

    if (!this.fDescendents.hasOwnProperty(key)) {
      this.fDescendents[key] = [];
    }

    this.fDescendents[key].push(entityNode);
  }

  GetEntity(key, i) {
    if (
      !this.fDescendents ||
      !this.fDescendents.hasOwnProperty(key) ||
      this.fDescendents[key].length <= i
    ) {
      return null;
    }

    return this.fDescendents[key][i];
  }

  GetEntities(key) {
    var entities = [];
    if (this.fDescendents && this.fDescendents.hasOwnProperty(key)) {
      entities = this.fDescendents[key];
    }

    return entities;
  }

  GetChildren() {
    var children = [];

    if (this.fDescendents) {
      for (var key in this.fDescendents) {
        children = children.concat(this.fDescendents[key]);
      }
    }

    return children;
  }

  RemoveEntities(key) {
    if (this.fDescendents && this.fDescendents.hasOwnProperty(key)) {
      this.fDescendents[key].length = 0;
      delete this.fDescendents[key];
    }
  }

  // Moves all children from otherEntity to this
  GrabChildren(otherEntity) {
    if (otherEntity.NumChildren() === 0) {
      return;
    }

    for (var key in otherEntity.fDescendents) {
      if (!this.fDescendents) {
        this.fDescendents = new Object();
      }
      if (!this.fDescendents.hasOwnProperty(key)) {
        this.fDescendents[key] = otherEntity.fDescendents[key].slice();
      } else {
        this.fDescendents[key] = this.fDescendents[key].concat(otherEntity.fDescendents[key]);
      }
      otherEntity.fDescendents[key].length = 0;
    }

    //TODO: update parent entity of all children to 'this'
  }

  ResetEntity(recursive) {
    // resets poly of this entity and its children
    if (recursive) {
      var children = this.GetChildren();
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        child.ResetEntity(recursive);
      }
    }

    this.InitBaseGeometry(0);
    this.ConstructPoly();
    this.UpdateForStaggeredStyle();
    this.fHasAuxline = false;
  }

  Clone() {
    var c = new NodeEntity();
    c.fBranchMgr = this.fBranchMgr;
    c.fParentNodeEntity = this.fParentNodeEntity;
    c.fNode = this.fNode;
    c.fID = this.fID;
    c.fEntityType = this.fEntityType;
    c.fBranchStyle = this.fBranchStyle;
    c.fSubBranchStyle = this.fSubBranchStyle;
    c.fShadow = this.fShadow;
    c.fHasAuxline = this.fHasAuxline;
    c.fBox = {
      fLeft: this.fBox.fLeft,
      fRight: this.fBox.fRight,
      fTop: this.fBox.fTop,
      fBottom: this.fBox.fBottom,
      fWidth: this.fBox.fWidth,
      fHeight: this.fBox.fHeight,
      fBorderThickness: this.fBox.fBorderThickness,
      fMargins: this.fBox.fMargins,
      fSpacing: this.fBox.fSpacing,
      fTopArrow: this.fBox.fTopArrow,
      fBottomArrow: this.fBox.fBottomArrow
    };
    c.fCalculatedPosition = this.fCalculatedPosition;
    c.fCorrectedChartHeight = this.fCorrectedChartHeight;

    if (Array.isArray(this.fAnchorUp)) {
      c.fAnchorUp = [];
      for (i = 0; i < this.fAnchorUp.length; i++) {
        c.fAnchorUp.push({ x: this.fAnchorUp[i].x, y: this.fAnchorUp[i].y });
      }
    } else {
      c.fAnchorUp = { x: this.fAnchorUp.x, y: this.fAnchorUp.y };
    }

    if (Array.isArray(this.fAnchorDown)) {
      c.fAnchorDown = [];
      for (i = 0; i < this.fAnchorDown.length; i++) {
        c.fAnchorDown.push({
          x: this.fAnchorDown[i].x,
          y: this.fAnchorDown[i].y
        });
      }
    } else {
      c.fAnchorDown = { x: this.fAnchorDown.x, y: this.fAnchorDown.y };
    }

    if (this.fDescendents) {
      for (var key in this.fDescendents) {
        for (var i = 0; i < this.fDescendents[key].length; i++) {
          var child = this.GetEntity(key, i);
          var cloneChild = child.Clone();
          c.AddEntity(key, cloneChild);
        }
      }
    }

    if (this.fMultiColumnJoints) {
      c.fMultiColumnJoints = [];
      for (i = 0; i < this.fMultiColumnJoints.length; i++) {
        c.fMultiColumnJoints.push(this.fMultiColumnJoints[i]);
      }
    }

    if (this.fNodePoly) {
      c.fNodePoly = this.fNodePoly.Clone();
    }

    if (this.fBasePoly) {
      c.fBasePoly = this.fBasePoly.Clone();
    }

    return c;
  }

  // it searches from this recursively until it finds an entity that matches
  // entityID. If not found, it returns null
  GetEntityByID(entityID) {
    var resultEntity = null;

    if (this.fID === entityID) {
      return this;
    }

    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      resultEntity = child.GetEntityByID(entityID);
      if (resultEntity) {
        return resultEntity;
      }
    }

    return resultEntity;
  }

  /**
   * @return {number}
   */
  GetWidth() {
    return this.fNodePoly.GetWidth();
  }

  /**
   * @return {number}
   */
  GetHeight() {
    return this.fNodePoly.GetHeight();
  }

  /**
   * @return {number}
   */
  GetContainerGutter() {
    var padding = 0;
    if (this.fNode) {
      padding = this.fNode.GetContainerCellPadding();
    }

    return padding;
  }

  //TOD
  GetInsideChildSpacing() {
    var spacing = new QRect();
    var fixedSpacing = 2;
    if (this.fNode) {
      var curSpacing = this.fNode.GetMemberBoxSpacing();
      //This functions as a object clone and a threshold for how far the member spacing can be.
      spacing.fLeft = curSpacing.fLeft > 0 ? fixedSpacing : 0;
      spacing.fRight = curSpacing.fRight > 0 ? fixedSpacing : 0;
      spacing.fTop = curSpacing.fTop > 0 ? fixedSpacing : 0;
      spacing.fBottom = curSpacing.fBottom > 0 ? fixedSpacing : 0;
    } else {
      spacing.InitRect(
        CARD_LAYOUT_SPACING,
        CARD_LAYOUT_SPACING,
        CARD_LAYOUT_SPACING,
        CARD_LAYOUT_SPACING
      );
    }

    return spacing;
  }

  GetContainerHeaderHeight() {
    var headerHeight = 0; // in case of header
    if (this.fNode) {
      headerHeight = this.fNode.GetLayout().CalcContentHeight();
      var gutterHeight = this.fNode.GetContainerCellPadding();
      if (headerHeight < 2 * gutterHeight) {
        headerHeight = 0;
      } else {
        headerHeight -= this.fNode.GetContainerCellPadding();
      }
      //This manual check here is because we do want a little padding between content and the rest of the box if a header exists.
    }
    return headerHeight;
  }

  GetContainerTopLinesToColumns() {
    return this.fNode ? this.fNode.GetContainerTopLinesToColumns() : false;
  }

  GetContainerBottomLinesToColumns() {
    return this.fNode ? this.fNode.GetContainerBottomLinesToColumns() : false;
  }

  GetContainerInternalLinesToColumns() {
    return this.fNode.GetContainerInternalLinesToColumns();
  }

  GetContainerNumColumns(pageWidth) {
    if (this.fNode) {
      var numRows = this.fNode.GetContainerMaxRows();
      var numColumns =
        numRows === 1 ? this.NumChildren("InsideChild") : this.fNode.GetContainerNumCols();
    } else {
      // this is going to be card style and number of columns depend on pageWidth
      var myInsideChildren = this.GetEntities("InsideChild");
      var widths = [];
      for (var i = 0; i < myInsideChildren.length; i++) {
        var child = myInsideChildren[i];
        widths.push(child.GetWidth() + CARD_LAYOUT_SPACING);
      }

      var containerWidth = 0;

      numColumns = myInsideChildren.length;
      for (i = 0; i < myInsideChildren.length; i++) {
        containerWidth += widths[i];
        if (containerWidth > pageWidth) {
          numColumns = i;
          break;
        }
      }

      function GridWidth(widthArray, nCol) {
        function ColumnWidth(widthArray, iCol, numCol) {
          var width = 0;
          for (var i = iCol; i < widthArray.length; i += numCol) {
            width = Math.max(width, widthArray[i]);
          }
          return width;
        }

        var gridWidth = 0;
        for (var i = 0; i < Math.min(widthArray.length, nCol); i++) {
          gridWidth += ColumnWidth(widthArray, i, nCol);
        }

        gridWidth += (nCol - 1) * CARD_LAYOUT_SPACING;
        return gridWidth;
      }

      for (i = numColumns; i > 0; i--) {
        if (GridWidth(widths, i) < pageWidth) break;
      }

      numColumns = i;
    }

    return numColumns;
  }

  GetAspectRatio() {
    return this.fNodePoly.fEnvelope.fWidth / this.fNodePoly.fEnvelope.fHeight;
  }

  /**
   * @return {number}
   */
  NumChildren(key) {
    var numChildren = 0;
    if (!this.fDescendents) {
      return numChildren;
    }

    if (typeof key === "undefined") {
      for (var key1 in this.fDescendents) {
        numChildren += this.fDescendents[key1].length;
      }
    } else {
      if (this.fDescendents.hasOwnProperty(key)) {
        numChildren = this.fDescendents[key].length;
      }
    }

    return numChildren;
  }

  GetMgrVSpacings() {
    var x = { upperSpacing: 0, lowerSpacing: 0 };
    x.upperSpacing = this.fNode.GetMgrVSpacing2();
    x.lowerSpacing = this.fNode.GetMgrVSpacing1();

    // if

    if (this.HasMultipleTopLines()) {
      // need to look at all inside children and use maximum of what we find as manager spacings.
      var insideEntities = this.GetEntities("InsideChild");
      x.lowerSpacing = Number.NEGATIVE_INFINITY;
      x.upperSpacing = Number.NEGATIVE_INFINITY;
      for (var i = 0; i < insideEntities.length; i++) {
        var spacing = insideEntities[i].GetMgrVSpacings();
        x.lowerSpacing = Math.max(x.lowerSpacing, spacing.lowerSpacing);
        x.upperSpacing = Math.max(x.upperSpacing, spacing.upperSpacing);
      }
    }

    return x;
  }

  /**
   * @return {boolean}
   */
  isMultiColumnStyle() {
    var numColumn = this.NumColumns();
    return numColumn >= 1;
  }

  /**
   * @return {number}
   */
  NumColumns() {
    var numColumns = 0;

    switch (this.fSubBranchStyle) {
      case enumBranchStyle.onecolumncenterleft:
      case enumBranchStyle.onecolumncenterright:
      case enumBranchStyle.onecolumnleft:
      case enumBranchStyle.onecolumnright:
        numColumns = 1;
        break;

      case enumBranchStyle.twocolumn:
        numColumns = 2;
        break;

      case enumBranchStyle.fourcolumn:
        numColumns = 4;
        break;

      case enumBranchStyle.sixcolumn:
        numColumns = 6;
        break;

      case enumBranchStyle.eightcolumn:
        numColumns = 8;
        break;

      case enumBranchStyle.twelvecolumn:
        numColumns = 12;
        break;

      case enumBranchStyle.sixteencolumn:
        numColumns = 16;
        break;

      case enumBranchStyle.twentycolumn:
        numColumns = 20;
        break;

      default:
        break;
    }

    return numColumns;
  }

  GetSubHeights() {
    var subHeights = [];
    var nSubs = this.NumChildren("Subordinate");

    for (var i = 0; i < nSubs; i++) {
      var sub = this.GetEntity("Subordinate", i);
      subHeights.push({ index: i, value: sub.fNodePoly.fEnvelope.fHeight });
    }

    return subHeights;
  }

  ResortSubsByIndex(newIndexArray) {
    // re-arrange subs based on this
    var sortedSubs = [];
    var nSubs = this.NumChildren("Subordinate");
    for (var i = 0; i < nSubs; i++) {
      sortedSubs.push(this.GetEntity("Subordinate", newIndexArray[i]));
    }

    this.ReplaceSubs(sortedSubs);
  }

  // replaces existing subs with new subs
  ReplaceSubs(subs) {
    var nSubs = this.NumChildren("Subordinate");
    if (subs.length !== nSubs) {
      return;
    }

    this.RemoveEntities("Subordinate");
    for (var i = 0; i < subs.length; i++) {
      this.fSubBranchStyle = subs[i].fBranchStyle;
      this.AddEntity("Subordinate", subs[i]);
    }
  }

  ReOrderContainerChildren() {
    // if container node has an associated order, it uses that for resorting the
    // subordinates

    var subIDOrder = this.fNode.GetOrderedChildrenIDs();
    if (subIDOrder.length === 0) {
      return;
    }

    //Sort the outside children
    /*
      var dataObj = this.fNode.GetRootDataObj();
      var parentObj = dataObj.GetParent();
      if (parentObj)
      {
          var orderedChildren  = [];
          var children = parentObj.GetChildList(false);
          var childrenIDs = children.map(function(a){return a.GetObjectID()});
          for (var aChildIDx in childrenIDs)
          {
              var theChildID = childrenIDs[aChildIDx];
              if (subIDOrder.indexOf(theChildID) > -1)
              {
                  orderedChildren.push(theChildID);
              }

          }
          subIDOrder = orderedChildren;
      }
      */
    var mySubs = this.GetEntities("Subordinate").slice(0);
    var newSubs = [];
    var subMap = new Map();
    for (var k = 0; k < mySubs.length; k++) {
      subMap.set(mySubs[k].fNode.GetObjectID(), mySubs[k]);
    }

    // here a container child of a container needs to be addressed
    for (var i = 0; i < subIDOrder.length; i++) {
      var sub = subMap.get(subIDOrder[i]);
      if (sub) {
        newSubs.push(sub);
        subMap.delete(subIDOrder[i]);
      }
    }

    // at the end there may be some IDs in subIDOreder which is no me importa
    // but if there are any subs left in mySubs, add them please.
    subMap.forEach(function (val) {
      newSubs.push(val);
    });
    this.ReplaceSubs(newSubs);
  }

  SetBranchStyle(branchStyle) {
    if (IsPureVerticalStyle(branchStyle) !== IsPureVerticalStyle(this.fBranchStyle)) {
      // branch style has changed in a way that the height might be different.
      var newBoxHeight = this.fNode.GetBoxHeightForStyle(branchStyle);
      var currentBoxHeight = this.fNode.GetBoxHeightForStyle(this.fBranchStyle);
      if (newBoxHeight !== currentBoxHeight) {
        this.InitBaseGeometry(newBoxHeight - currentBoxHeight);
        this.ConstructPoly();
        this.UpdateForStaggeredStyle();
      }
    }

    this.UpdateBranchStyle(branchStyle);
  }

  // changes the branch style of all subs
  SetSubBranchStyle(branchStyle) {
    // if we are in level chart, no other branch style exists, except index
    if (
      this.fBranchMgr.GetChartType() === enumChartTypes.level &&
      branchStyle !== enumBranchStyle.index
    ) {
      branchStyle = enumBranchStyle.tree;
    }

    // Normalize positioning styles to tree — ArrangeSubordinates has no handler.
    if (
      branchStyle === enumBranchStyle.swimlane ||
      branchStyle === enumBranchStyle.assistantpeerright ||
      branchStyle === enumBranchStyle.assistantpeerleft ||
      branchStyle === enumBranchStyle.assistantright ||
      branchStyle === enumBranchStyle.assistantleft
    ) {
      branchStyle = enumBranchStyle.tree;
    }

    if (branchStyle !== this.fSubBranchStyle) {
      this.fSubBranchStyle = branchStyle;
      var nSubs = this.NumChildren("Subordinate");
      for (var i = 0; i < nSubs; i++) {
        var sub = this.GetEntity("Subordinate", i);
        sub.SetBranchStyle(branchStyle);
      }
    }
    this.UpdateAnchorPoint();
  }

  GetBoxes(margin) {
    var boxes = [];
    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      boxes = boxes.concat(child.GetBoxes(margin));
    }

    var box = {
      left: this.fBox.fLeft - margin,
      right: this.fBox.fRight + margin,
      top: this.fBox.fTop - margin,
      bottom: this.fBox.fBottom + margin
    };

    //TODO: what to do with this anchors
    box.anchors = this.GetAuxlineAnchors().slice(0);
    boxes.push(box);

    return boxes;
  }

  SetAuxlineFlag(flag) {
    this.fHasAuxline = flag;
  }

  GetAuxlineAnchors() {
    //TODO: find out what update this needs to have
    var anchors = [];

    if (this.fHasAuxline) {
      var x1 = this.fBox.fLeft;
      var x2 = this.fBox.fRight;
      var y1 = this.fBox.fTop;
      var y2 = this.fBox.fBottom;
      var xm = 0.5 * (x1 + x2);
      var ym = 0.5 * (y1 + y2);
      var dx = 0.15 * (x2 - x1);
      var dy = 0.15 * (y2 - y1);

      var pt = {
        x: x1,
        y: Math.round(ym - dy),
        scorefactor: 1,
        side: "left"
      };
      if (
        this.fBranchStyle === enumBranchStyle.onecolumnright ||
        this.fBranchStyle === enumBranchStyle.onecolumncenterright
      ) {
        pt.scorefactor = 1.5;
      }
      anchors.push(pt);

      pt = {
        x: x1,
        y: Math.round(ym + dy),
        scorefactor: 1,
        side: "left"
      };
      if (
        this.fBranchStyle === enumBranchStyle.onecolumnright ||
        this.fBranchStyle === enumBranchStyle.onecolumncenterright
      ) {
        pt.scorefactor = 1.5;
      }
      anchors.push(pt);

      pt = {
        x: x2,
        y: Math.round(ym - dy),
        scorefactor: 1,
        side: "right"
      };
      if (
        this.fBranchStyle === enumBranchStyle.onecolumnleft ||
        this.fBranchStyle === enumBranchStyle.onecolumncenterleft
      ) {
        pt.scorefactor = 1.5;
      }
      anchors.push(pt);

      pt = {
        x: x2,
        y: Math.round(ym + dy),
        scorefactor: 1,
        side: "right"
      };
      if (
        this.fBranchStyle === enumBranchStyle.onecolumnleft ||
        this.fBranchStyle === enumBranchStyle.onecolumncenterleft
      ) {
        pt.scorefactor = 1.5;
      }
      anchors.push(pt);

      pt = {
        x: Math.round(xm - dx),
        y: y1,
        scorefactor: 1,
        side: "top"
      };
      anchors.push(pt);

      pt = {
        x: Math.round(xm + dx),
        y: y1,
        scorefactor: 1,
        side: "top"
      };
      anchors.push(pt);

      pt = {
        x: Math.round(xm - dx),
        y: y2,
        scorefactor: 1,
        side: "bottom"
      };
      anchors.push(pt);

      pt = {
        x: Math.round(xm + dx),
        y: y2,
        scorefactor: 1,
        side: "bottom"
      };
      anchors.push(pt);
    }

    return anchors;
  }

  InitBaseGeometry(heightOffset, updateAnchorPoints) {
    // reset values to defaults, then assign to proper values
    var baseRect = new QRect();
    baseRect.InitRect(-1, -1, 1, 1);
    var margins = new QRect();
    var spacing = new QRect();
    var topArrow = { height: 0, width: 0 };
    var bottomArrow = { height: 0, width: 0 };
    var borderThickness = 0;

    if (this.fNode) {
      baseRect = this.fNode.GetBaseRect().Clone();
      margins = this.fNode.GetBoxMargins().Clone();
      spacing = this.fNode.GetBoxSpacing().Clone();
      topArrow = this.GetSubChartBox(this, "top");
      bottomArrow = this.GetSubChartBox(this, "bottom");
      borderThickness = this.fNode.GetLayout().GetBorderOutset();
    }

    this.fBox = {
      fLeft: baseRect.fLeft,
      fRight: baseRect.fRight,
      fTop: baseRect.fTop,
      fBottom: baseRect.fBottom + heightOffset,
      fBorderThickness: borderThickness,
      fMargins: margins,
      fSpacing: spacing,
      fTopArrow: topArrow,
      fBottomArrow: bottomArrow
    };

    this.fBox.fWidth = this.fBox.fRight - this.fBox.fLeft;
    this.fBox.fHeight = this.fBox.fBottom - this.fBox.fTop;

    var center = this.Center();
    if (typeof updateAnchorPoints === "undefined") {
      this.fAnchorUp = {
        x: center.x,
        y: this.fBox.fTop
      };

      this.fAnchorDown = {
        x: this.XForSubs(),
        y: this.fBox.fBottom
      };
    }
  }

  ConstructPoly(extraSpacing) {
    if (typeof extraSpacing === "undefined") {
      extraSpacing = { top: 0, bottom: 0 };
    }

    var polyName = "xx";
    if (this.fNode) {
      polyName = this.fNode.GetID();
    }

    var myMargin = this.fBox.fMargins;
    var shadowOffset = this.fShadow ? SHADOW_OFFSET : 0;
    var box = {
      left: this.fBox.fLeft - myMargin.fLeft,
      right: this.fBox.fRight + myMargin.fRight + shadowOffset,
      top: this.fBox.fTop - myMargin.fTop - extraSpacing.top,
      bottom: this.fBox.fBottom + myMargin.fBottom + shadowOffset + extraSpacing.bottom
    };

    var polyColor = 0;

    if (!this.fNodePoly) {
      this.fNodePoly = new NodePoly();
    }
    this.fNodePoly.InitNodePolyFromBox(box, polyName, polyColor);

    var mySpacing = this.fBox.fSpacing;

    var centerPoint = this.Center();

    if (mySpacing.fLeft > myMargin.fLeft) {
      box = {
        left: this.fNodePoly.fEnvelope.fLeft - (mySpacing.fLeft - myMargin.fLeft),
        right: this.fNodePoly.fEnvelope.fLeft,
        top: centerPoint.y - 1,
        bottom: centerPoint.y + 1
      };
      var boxPoly = new NodePoly();
      boxPoly.InitNodePolyFromBox(box);
      this.fNodePoly.Merge(boxPoly, this.fNodePoly);
    }

    if (mySpacing.fRight > myMargin.fRight) {
      box = {
        left: this.fNodePoly.fEnvelope.fRight,
        right: this.fNodePoly.fEnvelope.fRight + (mySpacing.fRight - myMargin.fRight),
        top: centerPoint.y - 1,
        bottom: centerPoint.y + 1
      };
      boxPoly = new NodePoly();
      boxPoly.InitNodePolyFromBox(box);
      this.fNodePoly.Merge(this.fNodePoly, boxPoly);
    }

    if (
      this.fBox.fTopArrow.height > this.fBox.fMargins.fTop ||
      mySpacing.fTop > this.fBox.fMargins.fTop
    ) {
      if (this.fBox.fTopArrow.height > mySpacing.fTop) {
        var boxHalfWidth = Math.round(this.fBox.fTopArrow.width / 2);
        var boxHeight = this.fBox.fTopArrow.height - this.fBox.fMargins.fTop;
      } else {
        boxHalfWidth = 1;
        boxHeight = mySpacing.fTop - this.fBox.fMargins.fTop;
      }
      box = {
        left: centerPoint.x - boxHalfWidth,
        right: centerPoint.x + boxHalfWidth,
        top: this.fNodePoly.fEnvelope.fTop - boxHeight,
        bottom: this.fNodePoly.fEnvelope.fTop
      };
      boxPoly = new NodePoly();
      boxPoly.InitNodePolyFromBox(box);
      this.fNodePoly.Merge(boxPoly, this.fNodePoly);
    }

    if (
      this.fBox.fBottomArrow.height > this.fBox.fMargins.fBottom ||
      mySpacing.fBottom > this.fBox.fMargins.fBottom
    ) {
      if (this.fBox.fBottomArrow.height > mySpacing.fBottom) {
        boxHalfWidth = Math.round(this.fBox.fBottomArrow.width / 2);
        boxHeight = this.fBox.fBottomArrow.height - this.fBox.fMargins.fBottom;
      } else {
        boxHalfWidth = 1;
        boxHeight = mySpacing.fBottom - this.fBox.fMargins.fBottom;
      }
      box = {
        left: this.XForSubs() - boxHalfWidth,
        right: this.XForSubs() + boxHalfWidth,
        top: this.fNodePoly.fEnvelope.fBottom,
        bottom: this.fNodePoly.fEnvelope.fBottom + boxHeight
      };
      boxPoly = new NodePoly();
      boxPoly.InitNodePolyFromBox(box);
      this.fNodePoly.Merge(this.fNodePoly, boxPoly);
    }

    this.fBasePoly = this.fNodePoly.Clone();
  }

  GetCommonSubChartBox(type) {
    var subChartBox = { width: 0, height: 0 };
    if (!this.fNode) {
      return subChartBox;
    }

    var parent = this.fParentNodeEntity;
    subChartBox = this.GetSubChartBox(this, type);
    if (parent === null) {
      return subChartBox;
    }

    if (
      type === "bottom" &&
      (this.fBranchStyle === enumBranchStyle.tree || this.fBranchStyle === enumBranchStyle.stagger)
    ) {
      var siblings = parent.GetEntities("Subordinate");
      subChartBox = this.GetSubChartBox(siblings, "bottom");
    }

    return subChartBox;
  }

  GetSubChartBox(entities, type) {
    function GetSubChartBox(entity, type) {
      var subChartBox = { height: 0, width: 0 };
      if (entity.fNode) {
        var entityArrowInfo = entity.fNode.GetSCArrowInfo();
        if (entityArrowInfo.type === type) {
          subChartBox.height = entityArrowInfo.height;
        }

        subChartBox.width = entityArrowInfo.width;
      }

      // adjust box size for appropriate margins
      if (subChartBox.height > 0) {
        if (type === "top") {
          subChartBox.height += 5;
        }
        if (type === "bottom") {
          subChartBox.height += ADORNMENT_MARGIN;
        }
        subChartBox.width += 2 * ADORNMENT_MARGIN;
      }

      return subChartBox;
    }

    var subChartBox = { height: 0, width: 0 };
    if (Array.isArray(entities)) {
      for (var i = 0; i < entities.length; i++) {
        var entity = entities[i];
        var entitySubChartBox = GetSubChartBox(entity, type);
        subChartBox.height = Math.max(subChartBox.height, entitySubChartBox.height);
      }
    } else {
      subChartBox = GetSubChartBox(entities, type);
    }

    return subChartBox;
  }

  // Moves entity and all its children with amount of delta
  MoveEntityWithChildren(delta) {
    // first check against bad input
    if (
      delta.x === Number.POSITIVE_INFINITY ||
      delta.x === Number.NEGATIVE_INFINITY ||
      delta.y === Number.POSITIVE_INFINITY ||
      delta.y === Number.NEGATIVE_INFINITY
    ) {
      console.log("Asking to move to Infinity!! No way!");
      delta.x = 0;
      delta.y = 0;
    }

    // first move all children
    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      child.MoveEntityWithChildren(delta);
    }

    // now move yourself
    this.MoveEntity(delta, true);
  }

  // Moves entity with the amount of delta
  MoveEntity(delta, moveAnchors) {
    this.fNodePoly.MovePoly(delta);
    this.fBasePoly.MovePoly(delta);

    this.fBox.fLeft += delta.x;
    this.fBox.fRight += delta.x;
    this.fBox.fTop += delta.y;
    this.fBox.fBottom += delta.y;

    if (moveAnchors) {
      if (Array.isArray(this.fAnchorUp)) {
        for (var i = 0; i < this.fAnchorUp.length; i++) {
          this.fAnchorUp.splice(i, 1, {
            x: this.fAnchorUp[i].x + delta.x,
            y: this.fAnchorUp[i].y + delta.y
          });
        }
      } else {
        this.fAnchorUp.x += delta.x;
        this.fAnchorUp.y += delta.y;
      }

      if (Array.isArray(this.fAnchorDown)) {
        for (i = 0; i < this.fAnchorDown.length; i++) {
          this.fAnchorDown.splice(i, 1, {
            x: this.fAnchorDown[i].x + delta.x,
            y: this.fAnchorDown[i].y + delta.y
          });
        }
      } else {
        this.fAnchorDown.x += delta.x;
        this.fAnchorDown.y += delta.y;
      }

      if (this.fMultiColumnJoints) {
        for (i = 0; i < this.fMultiColumnJoints.length; i++) {
          this.fMultiColumnJoints[i] = this.fMultiColumnJoints[i] + delta.x;
        }
      }
    }
  }

  Center() {
    var center = {
      x: Math.round((this.fBox.fLeft + this.fBox.fRight) / 2),
      y: Math.round((this.fBox.fTop + this.fBox.fBottom) / 2)
    };

    return center;
  }

  AnchorLeft() {
    var anchor = {
      x: this.fBox.fLeft,
      y: this.Center().y
    };

    if (
      this.HasMultipleTopLines() &&
      (this.fBranchStyle === enumBranchStyle.assistantleft ||
        this.fBranchStyle === enumBranchStyle.assistantright)
    ) {
      anchor.y = this.TopAnchorPoint().y - COMANAGER_HEIGHT;
    }

    return anchor;
  }

  AnchorRight() {
    var anchor = {
      x: this.fBox.fRight,
      y: this.Center().y
    };

    if (
      this.HasMultipleTopLines() &&
      (this.fBranchStyle === enumBranchStyle.assistantleft ||
        this.fBranchStyle === enumBranchStyle.assistantright)
    ) {
      anchor.y = this.TopAnchorPoint().y - COMANAGER_HEIGHT;
    }
    return anchor;
  }

  /**
   * @return {number}
   */
  AssignCoordinates(recursive, pageWidth) {
    /*
       An entity does not now where it stands unless its children are known where they stand.
       1- entity asks all children to position themselves
       2- entity aligns them based on branch style and each children sizes
       3- entity updates its own poly

       done */
    var doRecalculate = false;

    if (recursive) {
      var children = this.GetChildren();
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        doRecalculate = doRecalculate || child.AssignCoordinates(recursive, 0);
      }
    }

    this.ArrangeInsideChildren(pageWidth);

    this.ArrangePeers();

    this.ArrangeAssistants();

    this.ArrangeSubordinates(pageWidth);

    // if this needs to be resized, one more arrange needs to happen
    if (this.fNode && this.fNode.IsSizingToChildren()) {
      var newWidth = this.GetSubsSpan();
      if (newWidth !== this.fNode.GetBoxWidth()) {
        this.fNode.SetBoxWidth(newWidth);
        doRecalculate = true;
      }
    }

    // now that coordinates are assigned, need to calculate corrected height and assign it to this
    // root and all children.
    var correctedHeight = this.CalculateCorrectedChartHeight();
    this.SetCorrectedHeight(correctedHeight);

    return doRecalculate;
  }

  ReAssignCoordinates(recursive) {
    this.ResetEntity(recursive);
    this.AssignCoordinates(recursive, 0);
  }

  // Page widths come to play only if we desire to expand table of inside children to width of the page
  ArrangeInsideChildren(pageWidth) {
    var numInsideChildren = this.NumChildren("InsideChild");
    if (numInsideChildren === 0) {
      return;
    }
    var myInsideChildren = this.GetEntities("InsideChild");

    //Sort the inside children
    //NOTE- THIS BREAKS INDEX PAGE - it assumes we preserve hierarchy (peers) with container
    /*
      var dataObj = this.fNode.GetRootDataObj();
      var parentObj = dataObj.GetParent();
      if (parentObj)
      {
          var insideChildrenIDs = myInsideChildren.map(function(a){return a.fID});
          var orderedChildren  = [];
          var children = parentObj.GetChildList(false);
          var childrenIDs = children.map(function(a){return a.GetObjectID()});
          for (var aChildIDx in childrenIDs)
          {
              var theChildID = childrenIDs[aChildIDx];
              var insideChildrenIDx = insideChildrenIDs.indexOf(theChildID);
              if (insideChildrenIDx > -1)
              {
                  var theInsideChild = myInsideChildren[insideChildrenIDx];
                  orderedChildren.push(theInsideChild);
              }

          }
          myInsideChildren = orderedChildren;
      }
      */
    //this.ReOrderContainerChildren();

    var headerHeight = this.GetContainerHeaderHeight();
    var containerGutter = this.GetContainerGutter();
    var alignPoint = { x: 0, y: 0 };

    var doCoMgrTopLines = this.GetContainerTopLinesToColumns();
    var doCoMgrBottomLines = this.GetContainerBottomLinesToColumns();
    var numColumns = this.GetContainerNumColumns(pageWidth);

    var compactCellWidths = true;

    var insidePoly = this.ArrangeTable(
      myInsideChildren,
      alignPoint,
      containerGutter,
      headerHeight,
      numColumns,
      compactCellWidths,
      doCoMgrTopLines,
      doCoMgrBottomLines
    );

    if (this.fNode === null) return;

    this.fNode.SetContainerBoxSize(insidePoly.fEnvelope.fWidth, insidePoly.fEnvelope.fHeight);

    // the header's expand row takes the box height holders don't use, so holders go below it
    const expandHeight = this.fNode.GetLayout().GetHolderExpandSpace();
    if (expandHeight > 0) {
      insidePoly = this.ArrangeTable(
        myInsideChildren,
        alignPoint,
        containerGutter,
        headerHeight + expandHeight,
        numColumns,
        compactCellWidths,
        doCoMgrTopLines,
        doCoMgrBottomLines
      );
    }

    this.ExpandEnvelopeToPositionBox(insidePoly.fEnvelope);

    var numSubordinates = this.NumChildren("Subordinate");

    var extraBottomSpace = 0;
    if (this.HasMultipleBottomLines() && this.fAnchorDown.length > 1 && numSubordinates > 1) {
      extraBottomSpace = COMANAGER_HEIGHT;
    }

    var extraTopSpace = 0;
    if (
      this.HasMultipleTopLines() &&
      (this.fBranchStyle === enumBranchStyle.assistantleft ||
        this.fBranchStyle === enumBranchStyle.assistantright)
    ) {
      extraTopSpace = 2 * COMANAGER_HEIGHT;
    }

    // reset values to defaults, then assign to proper values
    var margins = this.fNode.GetBoxMargins();
    var spacing = this.fBox.fSpacing; // this.fNode.GetBoxSpacing();
    var topArrow = this.GetSubChartBox(this, "top");
    var bottomArrow = this.GetSubChartBox(this, "bottom");
    var borderThickness = this.fNode.GetLayout().GetBorderOutset();
    var baseRect = insidePoly.fEnvelope;
    baseRect.fTop -= borderThickness;
    baseRect.fLeft -= borderThickness;
    baseRect.fBottom += borderThickness;
    baseRect.fRight += borderThickness;

    this.fBox = {
      fLeft: baseRect.fLeft,
      fRight: baseRect.fRight,
      fTop: baseRect.fTop,
      fBottom: baseRect.fBottom,
      fBorderThickness: borderThickness,
      fMargins: margins,
      fSpacing: spacing,
      fTopArrow: topArrow,
      fBottomArrow: bottomArrow
    };

    this.fBox.fWidth = this.fBox.fRight - this.fBox.fLeft;
    this.fBox.fHeight = this.fBox.fBottom - this.fBox.fTop;

    var center = this.Center();

    this.ConstructPoly({ top: extraTopSpace, bottom: extraBottomSpace });

    // need to move this entity to right place
    var delta = {
      x: insidePoly.fEnvelope.fLeft - this.fBox.fLeft,
      y: insidePoly.fEnvelope.fTop - this.fBox.fTop
    };

    this.MoveEntity(delta, false);
  }

  // Grow the inside envelope to the position box width (centered) so the collision poly matches
  // the drawn box, then rebase the connectors. No-op unless a position box is wider than holders.
  ExpandEnvelopeToPositionBox(envelope) {
    if (this.fNode === null || !this.fNode.IsPositionsNode()) return;

    var boxWidth = this.fNode.GetBoxWidth();
    var expansion = boxWidth - envelope.fWidth;
    if (expansion <= 0) return;

    var shift = Math.round(expansion / 2);
    envelope.fLeft -= shift;
    envelope.fRight = envelope.fLeft + boxWidth;
    envelope.fWidth = boxWidth;

    this.RecenterSingleAnchorsToEnvelope(envelope);
  }

  // Rebase the single connectors onto envelope; arrays (co-manager lines) stay tied to columns.
  RecenterSingleAnchorsToEnvelope(envelope) {
    if (this.fAnchorUp != null && !Array.isArray(this.fAnchorUp)) {
      this.fAnchorUp.x = Math.round((envelope.fLeft + envelope.fRight) / 2);
    }

    if (this.fAnchorDown != null && !Array.isArray(this.fAnchorDown)) {
      this.fAnchorDown.x = this.DownAnchorX(envelope.fLeft, envelope.fRight);
    }
  }

  // Bottom-connector X for the given box edges and sub-branch style
  DownAnchorX(left, right) {
    switch (this.fSubBranchStyle) {
      case enumBranchStyle.onecolumnleft:
        return right - ONE_COLUMN_OFFSET;
      case enumBranchStyle.onecolumnright:
        return left + ONE_COLUMN_OFFSET;
      case enumBranchStyle.onecolumncenterleft:
        return right;
      case enumBranchStyle.onecolumncenterright:
        return left;
      default:
        return Math.round((left + right) / 2);
    }
  }

  // Arranges all subordinates of this in a tree style fashion.
  ArrangeTreeStyle(subs, alignPoint, mgrSpacing) {
    var subsNodePoly = new NodePoly();

    if (subs === null || subs.length === 0) {
      return subsNodePoly;
    }

    var nSubs = subs.length;
    var subAlignPoint = {
      x: alignPoint.x,
      y: alignPoint.y + mgrSpacing.lowerSpacing + mgrSpacing.upperSpacing
    };
    subsNodePoly = this.AlignTop(subs, subAlignPoint, "top-center-boxes");
    var subCenterX = this.GetMiddleXforTreeSubs(subs);
    var subLeftX = subs[0].Center().x - 1;
    var subRightX = subs[nSubs - 1].Center().x + 1;
    this.AddTopPolys(
      subsNodePoly,
      subLeftX,
      subRightX,
      mgrSpacing.lowerSpacing,
      subCenterX,
      mgrSpacing.upperSpacing
    );

    return subsNodePoly;
  }

  GetBandLevel() {
    var bandLevel = Infinity;
    if (this.fEntityType === enumContainerType.standardmrb) {
      var myInsideChildren = this.GetEntities("InsideChild");
      myInsideChildren.forEach(function (item) {
        if (item.GetBandLevel() > -1) {
          bandLevel = Math.min(bandLevel, item.GetBandLevel());
        }
      });
    }

    if (this.fEntityType === enumContainerType.nonmrb) {
      bandLevel = this.fNode.GetNodeBandLevel().y;
      if (bandLevel === -1) bandLevel = Infinity;
    }

    return bandLevel;
  }

  AdjustSpacingsForLeveling(subs) {
    if (subs.length < 2) {
      return;
    }

    var levelInfoArr = [];
    var doLevelBanding = false;
    for (var i = 0; i < subs.length; i++) {
      var subBandLevel = subs[i].GetBandLevel();
      doLevelBanding = doLevelBanding || subBandLevel !== Infinity;
      levelInfoArr.push({
        level: subBandLevel,
        index: i,
        height:
          subs[i].fBox.fHeight +
          subs[i].fBox.fMargins.fTop +
          Math.max(subs[i].fBox.fMargins.fBottom, this.GetSubChartBox(subs[i], "bottom").height),
        topOffset: 6,
        bottomOffset: 0
      });
    }

    if (!doLevelBanding) return;

    // alright, we have met the conditions for leveling. Let's find out the appropriate shifts every sub needs to go down
    //var subLevelsSorted = levelInfoArr.sort(function(a,b){return a.level - b.level});
    var subLevelsSorted = SortUnique(levelInfoArr);

    for (i = 0; i < subLevelsSorted.length; i++) {
      var sameLevelArr = levelInfoArr.filter(SameLevel, {
        level: subLevelsSorted[i]
      });
      var sameLevelHeight = sameLevelArr.reduce(MaxHeightForLevel, 0);
      var higherLevelArr = levelInfoArr.filter(HigherLevel, {
        level: subLevelsSorted[i]
      });
      var lowerLevelArr = levelInfoArr.filter(LowerLevel, {
        level: subLevelsSorted[i]
      });

      higherLevelArr.forEach(function (item) {
        levelInfoArr[item.index].bottomOffset += sameLevelHeight;
      });

      lowerLevelArr.forEach(function (item) {
        levelInfoArr[item.index].topOffset += sameLevelHeight;
      });
    }

    for (i = 0; i < subs.length; i++) {
      var sub = subs[i];
      sub.fBox.fSpacing.fTop += levelInfoArr[i].topOffset;
      sub.fBox.fSpacing.fBottom += levelInfoArr[i].bottomOffset;
    }

    function SameLevel(element, index, arr) {
      return element.level === this.level;
    }

    function HigherLevel(element, index, arr) {
      return element.level < this.level;
    }

    function LowerLevel(element, index, arr) {
      return element.level > this.level;
    }

    function MaxHeightForLevel(maxHeight, item) {
      return Math.max(maxHeight, item.height);
    }

    function SortUnique(arr) {
      var outArr = [];
      for (var i = 0; i < arr.length; i++) {
        outArr.push(arr[i].level);
      }
      outArr = outArr.sort().filter(onlyUnique);
      return outArr;
    }
  }

  AlignTop(subs, alignPoint, alignPointType) {
    var subsNodePoly = new NodePoly();

    if (subs === null || subs.length === 0) {
      return subsNodePoly;
    }

    subsNodePoly = subs[0].fNodePoly.Clone();
    var nSubs = subs.length;
    var topPlateY = subs[0].fNodePoly.fEnvelope.fTop;

    if (nSubs > 1) {
      // First top align every sub to 1st one
      for (var i = 1; i < nSubs; i++) {
        var delta = { x: 0, y: topPlateY - subs[i].fNodePoly.fEnvelope.fTop };
        subs[i].MoveEntityWithChildren(delta);
      }

      // Now arrange all to be next to each other from left to right
      for (i = 1; i < nSubs; i++) {
        delta = { x: Number.NEGATIVE_INFINITY, y: 0 };
        for (var i0 = 0; i0 < i; i0++) {
          var hDistance = subs[i0].fNodePoly.HorizontalDistance(subs[i].fNodePoly);
          if (hDistance === Number.POSITIVE_INFINITY) {
            hDistance = subs[i0].fNodePoly.HorizontalDistance(subs[i].fNodePoly);
          }
          delta.x = Math.max(delta.x, -hDistance);
        }
        subs[i].MoveEntityWithChildren(delta);
        subsNodePoly.Merge(subsNodePoly, subs[i].fNodePoly);
      }
    }

    // we are done. Subs are nicely hugging each other. Need to move them to right position
    var myAlignPoint = { x: 0, y: 0 };
    switch (alignPointType) {
      case "top-right":
        myAlignPoint = {
          x: subsNodePoly.fEnvelope.fRight,
          y: subsNodePoly.fEnvelope.fTop
        };
        break;

      case "top-left":
        myAlignPoint = {
          x: subsNodePoly.fEnvelope.fLeft,
          y: subsNodePoly.fEnvelope.fTop
        };
        break;

      case "top-center":
        // this is for generic centering
        myAlignPoint = {
          x: Math.round((subsNodePoly.fEnvelope.fLeft + subsNodePoly.fEnvelope.fRight) / 2),
          y: subsNodePoly.fEnvelope.fTop
        };
        break;

      case "top-center-boxes":
        myAlignPoint = {
          x: this.GetMiddleXforTreeSubs(subs),
          y: subsNodePoly.fEnvelope.fTop
        };
        break;
    }

    delta = {
      x: alignPoint.x - myAlignPoint.x,
      y: alignPoint.y - myAlignPoint.y
    };
    for (i = 0; i < nSubs; i++) {
      subs[i].MoveEntityWithChildren(delta);
    }
    subsNodePoly.MovePoly(delta);

    return subsNodePoly;
  }

  // arranges all subordinates of 'this' in a one column fashion.
  // alignType is either "left" or "right" which means alignment after they are put on top of each other.
  // alignPoint is either topRight point for right or topLeft point for left alignment
  // it returns the polygon that encompasses 'this' subordinates
  ArrangeOneColumn(subs, alignPoint, alignType, extendedBottom, addMgrSpacing) {
    var subsNodePoly = new NodePoly();
    if (subs == null || subs.length === 0) {
      return subsNodePoly;
    }

    this.AlignBoxesHorizontally(subs, alignType);
    this.AddExtensionPoly(subs, alignType);
    this.StackOnTopOfEachOther(subs);
    this.BuildThinWallAndMoveSubs(subsNodePoly, subs, alignPoint, alignType, extendedBottom);
    if (addMgrSpacing) {
      var sub = subs[0];
      var subMargin = sub.fBox.fMargins;
      var subSpacing = sub.fBox.fSpacing;
      var centerX = alignPoint.x;
      var spacings = this.GetMgrVSpacings();

      switch (alignType) {
        case enumAlignStyle.left:
          var leftX = alignPoint.x;
          var rightX =
            subSpacing.fTop > subMargin.fTop
              ? sub.Center().x + 1
              : sub.fBox.fRight + subMargin.fRight;
          break;

        case enumAlignStyle.right:
          leftX =
            subSpacing.fTop > subMargin.fTop
              ? sub.Center().x - 1
              : sub.fBox.fLeft - subMargin.fLeft;
          rightX = alignPoint.x;
          break;
      }
      this.AddTopPolys(
        subsNodePoly,
        leftX,
        rightX,
        spacings.lowerSpacing,
        centerX,
        spacings.upperSpacing
      );
      // we are basically done. align to align point
      var delta = {
        x: 0,
        y: spacings.upperSpacing + spacings.lowerSpacing
      };
      for (var i = 0; i < subs.length; i++) {
        subs[i].MoveEntityWithChildren(delta);
      }
      subsNodePoly.MovePoly(delta);
    }

    return subsNodePoly;
  }

  ArrangeMultiColumn(subs, alignPoint, alignType, numColumns) {
    var subsNodePoly = new NodePoly();
    if (subs === null || subs.length === 0) {
      return subsNodePoly;
    }

    var n = subs.length;
    var subsColumn = [];

    // in normal multi-column style, style of columns alternate between right- and left-aligned.
    // create an array to keep track of it.
    var alignTypes = [];
    if (alignType === enumAlignStyle.alternate) {
      alignTypes.push(enumAlignStyle.right);
      for (var i = 1; i < numColumns; i++) {
        if (alignTypes[i - 1] === enumAlignStyle.right) {
          alignTypes.push(enumAlignStyle.left);
        } else {
          alignTypes.push(enumAlignStyle.right);
        }
      }
    } else {
      for (i = 0; i < numColumns; i++) {
        alignTypes.push(alignType);
      }
    }

    for (var column = 0; column < numColumns && column < n; column++) {
      subsColumn.length = 0;
      for (i = column; i < n; i += numColumns) {
        subsColumn.push(subs[i]);
      }

      this.AlignBoxesHorizontally(subsColumn, alignTypes[column]);
      this.AddExtensionPoly(subsColumn, alignTypes[column]);
    }

    // now need to stack them on top of each other, with an eye or horizontal spacing
    this.PutInGrid(subs, numColumns);

    // now need to put each column next to each other
    var retVal = this.PutColumnsNextToEachOther(subs, numColumns);
    subsNodePoly = retVal.poly;

    var nJoints = retVal.points.length;
    this.fMultiColumnJoints.length = 0;
    for (i = 0; i < nJoints; i++) {
      var x = retVal.points[i];
      this.fMultiColumnJoints.push(x);
    }

    // Add the top plates and spacing on top
    if (this.fNode) {
      var spacings = this.GetMgrVSpacings();
    } else {
      spacings = { upperSpacing: 10, lowerSpacing: 10 }; //TODO: romueve esta. No necessario
    }
    var iFirst = 0;
    var iLast = Math.min(n, numColumns) - 1;
    var leftX = subs[iFirst].Center().x - 1;
    var rightX = subs[iLast].Center().x + 1;
    if (Math.min(n, numColumns) === 1) {
      var centerX = subs[0].Center().x;
    } else {
      centerX = this.GetMiddleX(retVal.points);
    }

    this.AddTopPolys(
      subsNodePoly,
      leftX,
      rightX,
      spacings.lowerSpacing,
      centerX,
      spacings.upperSpacing
    );
    var topCenterPoint = { x: centerX, y: subsNodePoly.fEnvelope.fTop };

    // now everyone needs to move so topCenterPoint is on alignPoint
    var delta = {
      x: alignPoint.x - topCenterPoint.x,
      y: alignPoint.y - topCenterPoint.y
    };
    for (i = 0; i < n; i++) {
      subs[i].MoveEntityWithChildren(delta);
    }
    subsNodePoly.MovePoly(delta);

    for (i = 0; i < this.fMultiColumnJoints.length; i++) {
      this.fMultiColumnJoints[i] += delta.x;
    }

    return subsNodePoly;
  }

  // This one makes all subs to be arranged in a table grid.
  // If equalCellSizes is true the grid cells are of of exactly same height and same width, otherwise
  // They are arranged to be fit next to each other
  // Does not allow any encroachment to it.
  // returning poly is rectangle
  ArrangeTable(
    subs,
    alignPoint,
    gutter,
    headerHeight,
    numColumns,
    compactCellWidths,
    multipleTopLine,
    multipleBottomLine
  ) {
    var subsNodePoly = new NodePoly();
    if (subs === null || subs.length === 0) {
      return subsNodePoly;
    }

    // if equalWidth is true, it should position all boxes such a way that all of the centers are equi-distance
    // from each other. Otherwise, each column will have its own width.
    var colWidths = [];
    var rowHeights = [];
    var firstColShift = 0;
    var firstRowShift = 0;
    // First let's find the width and height of each table cell.
    var numSubs = subs.length;
    numColumns = Math.min(numColumns, numSubs);
    var numRows = Math.ceil(numSubs / numColumns);

    for (var i = 0; i < numColumns; i++) {
      colWidths.push(Number.NEGATIVE_INFINITY);
    }

    var rowArrowHeights = [];
    for (i = 0; i < numRows; i++) {
      rowHeights.push(Number.NEGATIVE_INFINITY);
      rowArrowHeights.push(0);
    }

    var trackArrowSpace =
      this.fEntityType === enumContainerType.standardmrb &&
      this.fNode &&
      this.fBranchMgr.fUsesMgrGrouping;

    var iCol = 0;
    var iRow = 0;
    for (i = 0; i < subs.length; i++) {
      var spacingRect = subs[i].GetInsideChildSpacing();
      if (!this.fNode) {
        spacingRect.InitRect(
          CARD_LAYOUT_SPACING,
          CARD_LAYOUT_SPACING,
          CARD_LAYOUT_SPACING,
          CARD_LAYOUT_SPACING
        );
      }

      if (compactCellWidths) {
        var width = subs[i].fBox.fWidth + spacingRect.fLeft + spacingRect.fRight;
        var height = subs[i].fBox.fHeight + spacingRect.fTop + spacingRect.fBottom;
        if (iCol === 0) {
          firstColShift = Math.min(firstColShift, -spacingRect.fLeft);
        }
        if (iRow === 0) {
          firstRowShift = Math.min(firstRowShift, -spacingRect.fTop);
        }
      } else {
        width = subs[i].GetWidth();
        height = subs[i].fBox.fHeight;
      }

      colWidths.splice(iCol, 1, Math.max(colWidths[iCol], width));
      rowHeights.splice(iRow, 1, Math.max(rowHeights[iRow], height));

      if (trackArrowSpace) {
        var bottomArrowH = subs[i].fBox.fBottomArrow?.height || 0;
        rowArrowHeights.splice(iRow, 1, Math.max(rowArrowHeights[iRow], bottomArrowH));
      }

      iCol++;
      if (iCol === numColumns) {
        iCol = 0;
        iRow++;
      }
    }

    // the subsPoly and subsequent positioning logic must respect smart height at this step if set
    const containerNode = this?.fNode;
    const containerLayout = containerNode?.GetLayout();
    const smartHeight =
      containerLayout && containerLayout.GetHeightSizingMode() === enumBoxHeightMode.autosmart
        ? containerLayout.GetSmartHeight()
        : 0;

    var totalWidth = arraySum(colWidths) + 2 * gutter + 2 * firstColShift;
    var totalHeight =
      arraySum(rowHeights) +
      arraySum(rowArrowHeights) +
      headerHeight +
      2 * gutter +
      2 * firstRowShift;
    var envelopeBox = {
      left: Math.round(alignPoint.x),
      right: Math.round(alignPoint.x) + totalWidth,
      top: Math.round(alignPoint.y),
      bottom: Math.round(alignPoint.y) + Math.max(totalHeight, smartHeight)
    };

    iRow = 0;
    iCol = 0;
    var xOffset = gutter;
    var yOffset = headerHeight + gutter;
    if (multipleTopLine) {
      this.fAnchorUp = [];
    }

    if (multipleBottomLine) {
      this.fAnchorDown = [];
    }

    for (i = 0; i < subs.length; i++) {
      var cellCenter = {
        x: Math.round(envelopeBox.left + xOffset + colWidths[iCol] / 2 + firstColShift),
        y: Math.round(envelopeBox.top + yOffset + rowHeights[iRow] / 2 + firstRowShift)
      };
      var delta = {
        x:
          cellCenter.x -
          subs[i].Center().x -
          (this.fBranchMgr.GetChartType() === enumChartTypes.card
            ? subs[i].fBox.fBorderThickness
            : 0),
        y:
          cellCenter.y -
          subs[i].Center().y -
          (this.fBranchMgr.GetChartType() === enumChartTypes.card
            ? subs[i].fBox.fBorderThickness
            : 0)
      };

      subs[i].MoveEntityWithChildren(delta);

      if (multipleTopLine && iRow === 0) {
        this.fAnchorUp.push({ x: cellCenter.x, y: subs[i].fBox.fTop });
      }

      if (multipleBottomLine && iRow === 0) {
        this.fAnchorDown.push({ x: cellCenter.x, y: subs[i].fBox.fBottom });
      }

      xOffset += colWidths[iCol];
      iCol++;
      if (iCol === numColumns) {
        iCol = 0;
        xOffset = gutter;
        yOffset += rowHeights[iRow] + rowArrowHeights[iRow];
        iRow++;
      }
    }

    subsNodePoly.InitNodePolyFromBox(envelopeBox);
    if (!multipleTopLine) {
      this.fAnchorUp = {
        x: Math.round((envelopeBox.left + envelopeBox.right) / 2),
        y: envelopeBox.top
      };
    }

    if (!multipleBottomLine) {
      this.fAnchorDown = {
        x: this.DownAnchorX(envelopeBox.left, envelopeBox.right),
        y: envelopeBox.bottom
      };
    }

    return subsNodePoly;
  }

  ArrangeBanded(subs, alignPoint, mgrSpacing) {
    var subsNodePoly = new NodePoly();
    if (subs === null || subs.length === 0) {
      return subsNodePoly;
    }

    // first find various bands and group subs accordingly
    var bandedSubs = this.GetBandedSubs(subs);
    // now bandedSubs have all subs of same band together

    var alignPointLocal = {
      x: alignPoint.x,
      y: alignPoint.y + mgrSpacing.lowerSpacing + mgrSpacing.upperSpacing
    };
    var bandPolys = [];
    var maxWidth = 0;

    //TODO: make different treatment for last one.
    for (var i = 0; i < bandedSubs.length; i++) {
      var band = bandedSubs[i];
      // break into left and right
      var bandLeft = [];
      var bandRight = [];
      for (var j = 0; j < band.length; j++) {
        if (j < band.length / 2 || i === bandedSubs.length - 1) {
          bandLeft.push(band[j]);
        } else {
          bandRight.push(band[j]);
        }
      }

      if (i !== bandedSubs.length - 1) {
        alignPointLocal.x = alignPoint.x - WALL_THICKNESS - 2;
        var subLeftPoly = this.AlignTop(bandLeft, alignPointLocal, "top-right");

        alignPointLocal.x = alignPoint.x + WALL_THICKNESS + 2;
        var subRightPoly = this.AlignTop(bandRight, alignPointLocal, "top-left");
        maxWidth = Math.max(maxWidth, subLeftPoly.GetWidth());
        maxWidth = Math.max(maxWidth, subRightPoly.GetWidth());
      } else {
        alignPointLocal.x = alignPoint.x;
        subLeftPoly = this.AlignTop(bandLeft, alignPointLocal, "top-center-boxes");
        subRightPoly = new NodePoly();
        maxWidth = Math.max(maxWidth, Math.round(subLeftPoly.GetWidth() / 2));
      }
      bandPolys.push({ left: subLeftPoly, right: subRightPoly });
      var bandHeight = Math.max(subLeftPoly.GetHeight(), subRightPoly.GetHeight());
      alignPointLocal.y = alignPointLocal.y + bandHeight + 12;
    }

    for (i = 0; i < bandPolys.length - 1; i++) {
      var polyLeft = bandPolys[i].left;
      var polyRight = bandPolys[i].right;
      bandHeight = Math.max(polyLeft.GetHeight(), polyRight.GetHeight()) + COMANAGER_HEIGHT;

      var boxLeft = {
        left: polyLeft.fEnvelope.fRight - maxWidth,
        right: polyLeft.fEnvelope.fRight + WALL_THICKNESS + 2,
        top: polyLeft.fEnvelope.fTop,
        bottom: polyLeft.fEnvelope.fTop + bandHeight
      };
      var ExpandedEnvLeft = new NodePoly();
      ExpandedEnvLeft.InitNodePolyFromBox(boxLeft);
      if (i === 0) {
        subsNodePoly = ExpandedEnvLeft.Clone();
      } else {
        subsNodePoly.Merge(subsNodePoly, ExpandedEnvLeft);
      }

      var boxRight = {
        left: boxLeft.right,
        right: boxLeft.right + maxWidth + WALL_THICKNESS + 2,
        top: boxLeft.top,
        bottom: boxLeft.bottom
      };
      var ExpandedEvnRight = new NodePoly();
      ExpandedEvnRight.InitNodePolyFromBox(boxRight);
      subsNodePoly.Merge(subsNodePoly, ExpandedEvnRight);
    }

    // last band treatment. Is different than others
    var polyLast = bandPolys[bandPolys.length - 1].left;
    var boxLast = {
      left: polyLast.fEnvelope.fLeft,
      right: polyLast.fEnvelope.fRight,
      top: polyLast.fEnvelope.fTop,
      bottom: polyLast.fEnvelope.fBottom
    };
    var polyEnvelopeLast = new NodePoly();
    polyEnvelopeLast.InitNodePolyFromBox(boxLast);

    if (bandPolys.length === 1) {
      subsNodePoly = polyEnvelopeLast.Clone();
    } else {
      subsNodePoly.Merge(subsNodePoly, polyEnvelopeLast);
    }

    // adding top poly to protect it from intrusion from other boxes
    var subCenterX = alignPoint.x;
    var subLeftX = alignPoint.x - maxWidth;
    var subRightX = alignPoint.y + maxWidth;
    this.AddTopPolys(
      subsNodePoly,
      subLeftX,
      subRightX,
      mgrSpacing.lowerSpacing,
      subCenterX,
      mgrSpacing.upperSpacing
    );

    return subsNodePoly;
  }

  AlignBoxesHorizontally(subs, alignType) {
    if (subs == null || subs.length < 2) {
      return;
    }

    var n = subs.length;
    // moving all subs such that boxes are lined up correctly. For this purpose, we make
    // all of them aligned with first one
    var XBoxAlign = 0;
    var polyNode = subs[0].fNodePoly;
    var box = subs[0].fBox;
    var margin = box.fMargins;
    var spacing = box.fSpacing;

    var leftExtent = box.fLeft - Math.max(spacing.fLeft, margin.fLeft);
    var rightExtent = box.fRight + Math.max(spacing.fRight, margin.fRight);

    switch (alignType) {
      case enumAlignStyle.left:
        XBoxAlign = leftExtent;
        break;

      case enumAlignStyle.right:
        XBoxAlign = rightExtent;
        break;

      case enumAlignStyle.center:
        XBoxAlign = Math.round((leftExtent + rightExtent) / 2);
        break;

      default:
        console.log("Not support align type in one column style");
        break;
    }

    for (var i = 1; i < n; i++) {
      polyNode = subs[i].fNodePoly;
      box = subs[i].fBox;
      margin = box.fMargins;
      spacing = box.fSpacing;
      leftExtent = box.fLeft - Math.max(spacing.fLeft, margin.fLeft);
      rightExtent = box.fRight + Math.max(spacing.fRight, margin.fRight);

      switch (alignType) {
        case enumAlignStyle.left:
          var delta = {
            x: XBoxAlign - leftExtent,
            y: 0
          };
          break;

        case enumAlignStyle.right:
          delta = {
            x: XBoxAlign - rightExtent,
            y: 0
          };
          break;

        case enumAlignStyle.center:
          delta = {
            x: XBoxAlign - Math.round((leftExtent + rightExtent) / 2),
            y: 0
          };
          break;

        default:
          console.log("Not support align type in one column style");
          break;
      }

      subs[i].MoveEntityWithChildren(delta);
    }
  }

  /**
   * @return {number}
   */
  NumTopConnectors() {
    var numConnectors = Array.isArray(this.fAnchorUp) ? this.fAnchorUp.length : 1;
    return numConnectors;
  }

  /**
   * @return {boolean}
   */
  ComanagerExists(subs) {
    var result = false;
    if (!subs || subs.length === 0 || this.fSubBranchStyle !== enumBranchStyle.tree) {
      return result;
    }

    for (var i = 0; i < subs.length; i++) {
      if (subs[i].NumTopConnectors() > 1) {
        result = true;
        break;
      }
    }

    return result;
  }

  /**
   * @return {boolean}
   */
  HasSubsWithComanager() {
    //TODO: What is replacement for this? It is used in page optimization to prevent the style change from tree style
    var subs = this.GetEntities("Subordinate");
    var result = this.ComanagerExists(subs);
    return result;
  }

  /**
   * @return {boolean}
   */
  HasMultipleTopLines() {
    var multipleTopLine = false;
    if (Array.isArray(this.fAnchorUp) && this.fAnchorUp.length > 0) {
      multipleTopLine = true;
    }

    return multipleTopLine;
  }

  /**
   * @return {boolean}
   */
  HasMultipleBottomLines() {
    var multipleBottomLine = false;
    if (Array.isArray(this.fAnchorDown) && this.fAnchorDown.length > 0) {
      multipleBottomLine = true;
    }

    return multipleBottomLine;
  }

  // this function adds right amount of poly so that they are left or right justified together
  AddExtensionPoly(subs, alignType) {
    if (subs === null || subs.length === 0) {
      return;
    }

    if (
      alignType !== enumAlignStyle.left &&
      alignType !== enumAlignStyle.right &&
      alignType !== enumAlignStyle.center
    ) {
      return;
    }

    var n = subs.length;
    var extremeXLeft = Number.POSITIVE_INFINITY;
    var extremeXRight = Number.NEGATIVE_INFINITY;

    for (var i = 0; i < n; i++) {
      var polyNode = subs[i].fNodePoly;
      var envelope = polyNode.fEnvelope;
      var box = subs[i].fBox;
      var margin = box.fMargins;
      var spacing = box.fSpacing;
      var leftMost =
        i === n - 1 ? box.fLeft - Math.max(spacing.fLeft, margin.fLeft) : envelope.fLeft;
      extremeXLeft = Math.min(extremeXLeft, leftMost);

      var rightMost =
        i === n - 1 ? box.fRight + Math.max(spacing.fRight, margin.fRight) : envelope.fRight;
      extremeXRight = Math.max(extremeXRight, rightMost);
    }

    // creating extension polys
    for (i = 0; i < n; i++) {
      var center = subs[i].Center();
      polyNode = subs[i].fNodePoly;
      box = subs[i].fBox;
      margin = box.fMargins;
      spacing = box.fSpacing;
      var shadowOffset = subs[i].fShadow ? SHADOW_OFFSET : 0;
      var extensionPoly = new NodePoly();
      var extensionBox = {
        left: 0,
        right: 0,
        top: center.y - 1,
        bottom: center.y + 1
      };
      switch (alignType) {
        case enumAlignStyle.left:
          extensionBox.left = extremeXLeft;
          extensionBox.right = box.fLeft - Math.max(spacing.fLeft, margin.fLeft);
          if (extensionBox.right > extensionBox.left) {
            extensionPoly.InitNodePolyFromBox(extensionBox);
            polyNode.Merge(extensionPoly, polyNode);
          }
          break;

        case enumAlignStyle.right:
          extensionBox.left = box.fRight + Math.max(spacing.fRight, margin.fRight + shadowOffset);
          extensionBox.right = extremeXRight;
          if (extensionBox.right > extensionBox.left) {
            extensionPoly.InitNodePolyFromBox(extensionBox);
            polyNode.Merge(polyNode, extensionPoly);
          }
          break;

        case enumAlignStyle.center:
          extensionBox.left = extremeXLeft;
          extensionBox.right = box.fLeft - Math.max(spacing.fLeft, margin.fLeft);
          if (extensionBox.right > extensionBox.left) {
            extensionPoly.InitNodePolyFromBox(extensionBox);
            polyNode.Merge(extensionPoly, polyNode);
          }

          extensionBox.left = box.fRight + Math.max(spacing.fRight, margin.fRight + shadowOffset);
          extensionBox.right = extremeXRight;
          if (extensionBox.right > extensionBox.left) {
            extensionPoly.InitNodePolyFromBox(extensionBox);
            polyNode.Merge(polyNode, extensionPoly);
          }
          break;

        default:
          console.log("Not support align type in one column style");
          break;
      }
    }
  }

  StackOnTopOfEachOther(subs) {
    if (subs === null || subs.length < 2) {
      return;
    }

    var n = subs.length;

    for (var i = 1; i < n; i++) {
      var polyNode = subs[i].fNodePoly;
      var vDistance = Number.POSITIVE_INFINITY;
      for (var i0 = 0; i0 < i; i0++) {
        var topPoly = subs[i0].fNodePoly;
        vDistance = Math.min(vDistance, topPoly.VerticalDistance(polyNode));
      }

      if (vDistance !== Number.POSITIVE_INFINITY) {
        var delta = { x: 0, y: -vDistance };
        subs[i].MoveEntityWithChildren(delta);
      }
    }
  }

  PutInGrid(subs, numColumns) {
    if (subs === null || subs.length === 0) {
      return;
    }

    var n = subs.length;

    for (var i = 0; i < n; i += numColumns) {
      this.AlignRowInGrid(subs, numColumns, i);
      for (var ic = 0; ic < numColumns && i + ic < n; ic++) {
        this.PushEntitiesDown(subs, numColumns, i + ic);
      }
    }
  }

  PutColumnsNextToEachOther(subs, numColumns) {
    var n = subs.length;

    var topPlate = Number.POSITIVE_INFINITY;
    for (var i = 0; i < numColumns && i < n; i++) {
      topPlate = Math.min(topPlate, subs[i].fNodePoly.fEnvelope.fTop);
    }

    var subPolyColumnArray = [];
    var columnBottoms = [];
    var subColumn = [];
    var alignType = enumAlignStyle.right;
    for (i = 0; i < numColumns && i < n; i++) {
      subColumn.length = 0;
      for (var i0 = i; i0 < n; i0 += numColumns) {
        subColumn.push(subs[i0]);
      }
      var subPolyColumn = new NodePoly();

      var alignPoint = { x: 0, y: subs[i].fNodePoly.fEnvelope.fTop - topPlate };
      this.BuildThinWallAndMoveSubs(subPolyColumn, subColumn, alignPoint, alignType, false);
      subPolyColumnArray.push(subPolyColumn);
      var lastSub = subColumn[subColumn.length - 1];
      columnBottoms.push(lastSub.Center().y + 1);
      alignType = enumAlignStyle.right === alignType ? enumAlignStyle.left : enumAlignStyle.right;
    }

    // put them next to each other
    var subPoly = new NodePoly();
    var points = [];
    points[0] = 0;
    subPoly = subPolyColumnArray[0].Clone();
    for (i = 1; i < numColumns && i < n; i++) {
      var rightPoly = subPolyColumnArray[i];
      var hDistance = subPoly.HorizontalDistance(rightPoly);

      // move this poly and all nodes within it to the right by this much
      var delta = { x: -hDistance, y: 0 };
      for (i0 = i; i0 < n; i0 += numColumns) {
        subs[i0].MoveEntityWithChildren(delta);
      }
      rightPoly.MovePoly(delta);
      subPoly.Merge(subPoly, rightPoly);
      if (i % 2 === 0) {
        points.push(-hDistance);
      } else if (columnBottoms[i] > columnBottoms[i - 1]) {
        points[points.length - 1] = -hDistance;
      }
    }

    return { poly: subPoly, points: points };
  }

  AlignRowInGrid(subs, numColumns, iElement) {
    // starting from iElement (which should be in first column, moves all elements of other columns so that they are
    // horizontally aligned to the lowest item

    var n = subs.length;
    var lowestTop = Number.NEGATIVE_INFINITY;
    for (var i = iElement; i < iElement + numColumns && i < n; i++) {
      var envelope = subs[i].fNodePoly.fEnvelope;
      lowestTop = Math.max(lowestTop, envelope.fTop);
    }

    for (i = iElement; i < iElement + numColumns, i < n; i++) {
      envelope = subs[i].fNodePoly.fEnvelope;
      var delta = { x: 0, y: lowestTop - envelope.fTop };
      if (delta.y > 0) {
        // now move ith element down
        subs[i].MoveEntityWithChildren(delta);
      }
    }
  }

  PushEntitiesDown(subs, numColumns, iElement) {
    var n = subs.length;
    var subsInColumn = [];
    for (var i = iElement; i < n; i += numColumns) {
      subsInColumn.push(subs[i]);
    }
    this.StackOnTopOfEachOther(subsInColumn);
  }

  BuildThinWallAndMoveSubs(subPoly, subs, alignPoint, alignType, extendToBottom) {
    if (subs === null || subs.length === 0) {
      return;
    }

    var n = subs.length;

    // first move all subs vertically to be aligned with alignPoint
    var envelope = subs[0].fNodePoly.fEnvelope;
    var delta = { x: 0, y: alignPoint.y - envelope.fTop };
    for (var i = 0; i < n; i++) {
      subs[i].MoveEntityWithChildren(delta);
    }

    var lastSub = subs[n - 1];
    var wallBottom = extendToBottom ? lastSub.fNodePoly.fEnvelope.fBottom : lastSub.Center().y + 1;
    switch (alignType) {
      case enumAlignStyle.left:
        var wallBox = {
          left: alignPoint.x,
          right: alignPoint.x + WALL_THICKNESS,
          top: alignPoint.y,
          bottom: wallBottom
        };
        break;

      case enumAlignStyle.right:
        wallBox = {
          left: alignPoint.x - WALL_THICKNESS,
          right: alignPoint.x,
          top: alignPoint.y,
          bottom: wallBottom
        };
        break;

      case enumAlignStyle.center:
        // no wall is needed for center alignment
        break;

      default:
        console.log("Not support align type in one column style");
        return;
        break;
    }

    if (alignType === enumAlignStyle.left || alignType === enumAlignStyle.right) {
      var wallPoly = new NodePoly();
      wallPoly.InitNodePolyFromBox(wallBox);
      subPoly.InitNodePolyFromBox(wallBox);
    } else {
      subPoly.CloneFromOther(subs[0].fNodePoly);
    }

    for (i = 0; i < n; i++) {
      var nodePoly = subs[i].fNodePoly;
      switch (alignType) {
        case enumAlignStyle.left:
          var hDistance = wallPoly.HorizontalDistance(nodePoly);
          delta = { x: -hDistance, y: 0 };
          subs[i].MoveEntityWithChildren(delta);
          subPoly.Merge(subPoly, subs[i].fNodePoly);
          break;

        case enumAlignStyle.right:
          hDistance = nodePoly.HorizontalDistance(wallPoly);
          delta = { x: hDistance, y: 0 };
          subs[i].MoveEntityWithChildren(delta);
          if (i === 0) {
            subPoly.Merge(subs[i].fNodePoly, subPoly);
          } else {
            subPoly.Merge(subs[i].fNodePoly, subPoly);
            //subPoly.Merge(subPoly, subs[i].fNodePoly);
          }
          break;

        case enumAlignStyle.center:
          subPoly.Merge(subPoly, subs[i].fNodePoly);
          break;

        default:
          console.log("Not support align type in one column style");
          break;
      }
    }
  }

  // arranges peer left and right, then creates necessary walls that are
  // used for subsequent positioning of other types of stuff.
  // dividingLine is used when it's needed. It causes the left and right peers to go left
  // or right if necessary
  ArrangePeers() {
    if (this.NumChildren("PeerLeft") === 0 && this.NumChildren("PeerRight") === 0) {
      return;
    }

    var dividingX = this.XforAssists();

    // do we need a dividing wall
    var dividingWallNeeded = false;
    if (
      (this.NumChildren("PeerLeft") === 1 && this.NumChildren("PeerRight") === 1) ||
      this.NumChildren("AssistantLeft") > 0 ||
      this.NumChildren("AssistantRight") > 0 ||
      this.NumChildren("Subordinate") > 0
    ) {
      dividingWallNeeded = true;
    }
    var peerLeft = null;
    var peerLeftPoly = null;
    // first the left one. It only makes sense if one and only one peer left exists
    if (this.NumChildren("PeerLeft") === 1) {
      peerLeft = this.GetEntity("PeerLeft", 0);
      var myMargin = this.fBox.fMargins;
      var delta = {
        x: 0,
        y: this.fBox.fTop - myMargin.fTop - peerLeft.fNodePoly.fEnvelope.fTop
      };
      peerLeft.MoveEntityWithChildren(delta);
      peerLeftPoly = peerLeft.fNodePoly;
    }

    var peerRight = null;
    var peerRightPoly = null;
    // now, let's move the right one. It only makes sense if one and only one peer right exists
    if (this.NumChildren("PeerRight") === 1) {
      peerRight = this.GetEntity("PeerRight", 0);
      myMargin = this.fBox.fMargins;
      delta = {
        x: 0,
        y: this.fBox.fTop - myMargin.fTop - peerRight.fNodePoly.fEnvelope.fTop
      };
      peerRight.MoveEntityWithChildren(delta);
      peerRightPoly = peerRight.fNodePoly;
    }

    var myPoly = this.fNodePoly;

    // now, we need to build the wall and move left and right accordingly.
    if (dividingWallNeeded) {
      var lowestPoint = this.fNodePoly.fEnvelope.fBottom;

      if (peerLeftPoly) {
        lowestPoint = Math.max(lowestPoint, peerLeftPoly.fEnvelope.fBottom);
      }
      if (peerRightPoly) {
        lowestPoint = Math.max(lowestPoint, peerRightPoly.fEnvelope.fBottom);
      }

      if (lowestPoint > this.fNodePoly.fEnvelope.fBottom) {
        var wallBox = {
          left: dividingX - 1,
          right: dividingX + 1,
          top: this.fNodePoly.fEnvelope.fBottom,
          bottom: lowestPoint
        };

        var wallPoly = new NodePoly();
        wallPoly.InitNodePolyFromBox(wallBox);
        myPoly.Merge(myPoly, wallPoly);
      }
    }

    // move left one left, right one right
    if (peerLeftPoly) {
      var hDistance = peerLeftPoly.HorizontalDistance(myPoly);
      delta = { x: hDistance, y: 0 };
      peerLeft.MoveEntityWithChildren(delta);
      peerLeftPoly = peerLeft.fNodePoly;
    }

    if (peerRightPoly) {
      hDistance = myPoly.HorizontalDistance(peerRightPoly);
      delta = { x: -hDistance, y: 0 };
      peerRight.MoveEntityWithChildren(delta);
      peerRightPoly = peerRight.fNodePoly;
    }

    // merge the two polys
    if (myPoly) {
      myPoly.Merge(myPoly, peerRightPoly, "top");
      myPoly.Merge(peerLeftPoly, myPoly, "top");
    }

    //TODO: Decide on what to do with anchor point down
  }

  ArrangeAssistants() {
    var numLefts = this.NumChildren("AssistantLeft");
    var numRights = this.NumChildren("AssistantRight");
    if (numLefts === 0 && numRights === 0) {
      return;
    }

    var alignPoint = { x: this.XforAssists(), y: this.YforAssists() };

    var myPoly = this.fNodePoly;
    // left assistants first
    var assistLeftPoly = this.ArrangeOneColumn(
      this.fDescendents["AssistantLeft"],
      alignPoint,
      enumAlignStyle.right,
      true,
      false
    );

    // right assistants
    var assistRightPoly = this.ArrangeOneColumn(
      this.fDescendents["AssistantRight"],
      alignPoint,
      enumAlignStyle.left,
      true,
      false
    );

    // any cleaning that needs to happen here
    myPoly.Merge(myPoly, assistLeftPoly);
    myPoly.Merge(myPoly, assistRightPoly);
    //this.fAnchorPoint.y = this.fNodePoly.fEnvelope.fBottom;
    //TODO: update AnchorPointDown
  }

  ArrangeSubordinates(pageWidth) {
    var numSubs = this.NumChildren("Subordinate");
    if (numSubs === 0) {
      return;
    }

    var subsPoly;
    var mySubs = this.GetEntities("Subordinate");
    var subBranchStyle = this.fSubBranchStyle;
    var alignPoint = { x: this.BottomAnchorPoint().x, y: this.YForSubs() };
    var mgrSpacing = this.GetMgrVSpacings();

    switch (subBranchStyle) {
      case enumBranchStyle.tree:
      case enumBranchStyle.stagger:
        if (this.HasSubsWithComanager()) {
          mgrSpacing.lowerSpacing += COMANAGER_HEIGHT;
        }
        subsPoly = this.ArrangeTreeStyle(mySubs, alignPoint, mgrSpacing);
        break;

      case enumBranchStyle.banded:
        subsPoly = this.ArrangeBanded(mySubs, alignPoint, mgrSpacing);
        break;
      case enumBranchStyle.twocolumn:
      case enumBranchStyle.fourcolumn:
      case enumBranchStyle.sixcolumn:
      case enumBranchStyle.eightcolumn:
      case enumBranchStyle.twelvecolumn:
      case enumBranchStyle.sixteencolumn:
      case enumBranchStyle.twentycolumn:
        var numColumns = this.NumColumns();
        var alignType = enumAlignStyle.alternate;
        subsPoly = this.ArrangeMultiColumn(mySubs, alignPoint, alignType, numColumns);
        break;

      case enumBranchStyle.onecolumncenterright:
        subsPoly = this.ArrangeOneColumn(mySubs, alignPoint, enumAlignStyle.left, false, true);
        break;

      case enumBranchStyle.onecolumncenterleft:
        subsPoly = this.ArrangeOneColumn(mySubs, alignPoint, enumAlignStyle.right, false, true);
        break;

      case enumBranchStyle.onecolumnright:
        subsPoly = this.ArrangeOneColumn(mySubs, alignPoint, enumAlignStyle.left, false, true);
        break;

      case enumBranchStyle.onecolumnleft:
        subsPoly = this.ArrangeOneColumn(mySubs, alignPoint, enumAlignStyle.right, false, true);
        break;
      case enumBranchStyle.index:
        subsPoly = this.ArrangeMultiColumn(mySubs, alignPoint, enumAlignStyle.center, 4);
        break;
      default:
        console.log("unsupported branch style for subordinates");
        return;
    }

    // before merging, resize box and move align point accordingly
    var newAlignPoint = { x: this.XForSubs(), y: this.YForSubs() };
    var doShift = false;
    if (this.fNode && this.fNode.IsSizingToChildren()) {
      var newWidth = this.GetSubsSpan();

      // return to the caller if the size is going to be redone. The caller will call the rest of this
      // code with a slight different logic.
      if (newWidth !== this.fNode.GetBoxWidth()) {
        return;
      }
    }

    var xCenterOfChart = Math.round((subsPoly.fEnvelope.fRight + subsPoly.fEnvelope.fLeft) / 2);
    var xCenterOfParent = this.XForSubs();
    var xShift = xCenterOfParent - xCenterOfChart;

    // one more check before we are completely done.
    if (
      this.fNode &&
      this.fParentNodeEntity === null &&
      this.fSubBranchStyle === enumBranchStyle.tree &&
      needToRecenterToPage &&
      pageWidth !== 0
    ) {
      newAlignPoint.x = newAlignPoint.x + xShift;
      newAlignPoint.x = this.GetFittedXForSubs(newAlignPoint.x, mySubs);
      doShift = true;
    }

    if (doShift) {
      for (var i = 0; i < mySubs.length; i++) {
        mySubs[i].MoveEntityWithChildren({
          x: newAlignPoint.x - alignPoint.x,
          y: 0
        });
        subsPoly.MovePoly({ x: newAlignPoint.x - alignPoint.x, y: 0 });
      }
    }

    this.fNodePoly.Merge(this.fNodePoly, subsPoly);
  }

  GetSubsSpan() {
    var width = this.fBox.fWidth;
    var minWidth = this.fNode.GetLayout().GetWidthMin();
    var mySubs = this.GetEntities("Subordinate");
    if (mySubs.length === 0) {
      width = minWidth;
      return width;
    }

    var minX = Number.POSITIVE_INFINITY;
    var maxX = Number.NEGATIVE_INFINITY;
    for (var i = 0; i < mySubs.length; i++) {
      minX = Math.min(minX, mySubs[i].fBox.fLeft);
      maxX = Math.max(maxX, mySubs[i].fBox.fRight);
    }

    width = maxX - minX;
    return width;
  }

  GetNodeLayout() {
    if (this.fNode) return this.fNode.GetLayout();

    // otherwise, see if there is at least one inside child with fNode, return its layout

    var insideChild = this.GetEntity("InsideChild", 0);
    if (insideChild !== null) return insideChild.GetNodeLayout();

    return null;
  }

  GetBandedSubs(subs) {
    var bandedSubs = [];
    // first find various bands and group subs accordingly
    for (var i = 0; i < subs.length; i++) {
      var sub = subs[i];
      var bandNumber = sub.fNode.GetNodeBandLevel();
      // for now only y is of interest
      var bandLevel = bandNumber.y;
      if (bandLevel >= bandedSubs.length) {
        var numToAdd = bandLevel - bandedSubs.length + 1;
        for (var j = 0; j < numToAdd; j++) {
          var tempBandSubs = [];
          bandedSubs.push(tempBandSubs);
        }
      }

      bandedSubs[bandLevel].push(sub);
    }

    // now bandedSubs have all same band levels together, but there can be empty ones.
    // purge empty ones
    for (i = 0; i < bandedSubs.length; i++) {
      if (bandedSubs[i].length === 0) {
        bandedSubs.splice(i, 1);
        i--;
      }
    }

    return bandedSubs;
  }

  AddTopPolys(subPoly, plate1LeftX, plate1RightX, plate1Height, plate2CenterX, plate2Height) {
    var topOfPoly = subPoly.fEnvelope.fTop;
    if (plate1Height > 0) {
      var topBox = {
        left: plate1LeftX,
        right: plate1RightX,
        top: topOfPoly - plate1Height,
        bottom: topOfPoly
      };
      var topPoly = new NodePoly();
      topPoly.InitNodePolyFromBox(topBox);
      subPoly.Merge(topPoly, subPoly);
      topOfPoly = subPoly.fEnvelope.fTop;
    }

    if (plate2Height > 0) {
      topBox = {
        left: plate2CenterX - 1,
        right: plate2CenterX + 1,
        top: topOfPoly - plate2Height,
        bottom: topOfPoly
      };
      topPoly = new NodePoly();
      topPoly.InitNodePolyFromBox(topBox);
      subPoly.Merge(topPoly, subPoly);
    }
  }

  /**
   * @return {number}
   */
  XForSubs() {
    //TODO: this probably is going to be replaced or depricated
    var X = this.Center().x;
    if (this.NumChildren("Subordinate") === 0) {
      return X;
    }

    // if there is no assistants AND the type of subordinates is one-column right or left, this is different

    if (this.NumChildren("AssistantLeft") > 0 || this.NumChildren("AssistantRight") > 0) {
      return X;
    }

    var branchStyle = this.fSubBranchStyle;
    switch (branchStyle) {
      case enumBranchStyle.onecolumnleft:
        X = this.fBox.fRight - ONE_COLUMN_OFFSET;
        break;

      case enumBranchStyle.onecolumnright:
        X = this.fBox.fLeft + ONE_COLUMN_OFFSET;
        break;

      case enumBranchStyle.onecolumncenterleft:
        X = this.fBox.fRight;
        break;

      case enumBranchStyle.onecolumncenterright:
        X = this.fBox.fLeft;
        break;
    }

    return X;
  }

  BottomAnchorPoint() {
    var anchor = { x: 0, y: 0 };
    if (Array.isArray(this.fAnchorDown) && this.fAnchorDown.length > 0) {
      var xArray = [];
      for (var i = 0; i < this.fAnchorDown.length; i++) {
        xArray.push(this.fAnchorDown[i].x);
      }
      anchor.x = this.GetMiddleX(xArray);
      anchor.y = this.fAnchorDown[0].y;
      if (this.fAnchorDown.length > 1) {
        anchor.y += COMANAGER_HEIGHT;
      }
    } else if (this.fAnchorDown != null) {
      anchor = this.fAnchorDown;
    }

    return anchor;
  }

  TopAnchorPoint() {
    var anchor = { x: 0, y: 0 };
    if (Array.isArray(this.fAnchorUp) && this.fAnchorUp.length > 0) {
      var xArray = [];
      for (var i = 0; i < this.fAnchorUp.length; i++) {
        xArray.push(this.fAnchorUp[i].x);
      }
      anchor.x = this.GetMiddleX(xArray);
      anchor.y = this.fAnchorUp[0].y;
      if (this.fAnchorUp.length > 1) {
        anchor.y -= COMANAGER_HEIGHT;
      }
    } else if (this.fAnchorUp != null) {
      anchor = this.fAnchorUp;
    }

    return anchor;
  }

  BottomConnectorPoint() {
    var connector = this.BottomAnchorPoint();

    // special case for two styles
    if (
      this.fSubBranchStyle === enumBranchStyle.onecolumncenterright ||
      this.fSubBranchStyle === enumBranchStyle.onecolumncenterleft
    ) {
      connector.x = Math.round((this.fBox.fLeft + this.fBox.fRight) / 2);
    }

    return connector;
  }

  BottomConnectingLineExists() {
    var result = false;
    if (!this.HasMultipleBottomLines()) {
      return result;
    }

    // conditions not to draw multiple bottom lines
    var bottomLinesCount = this.fAnchorDown.length;
    var subs = this.GetEntities("Subordinate");
    result =
      bottomLinesCount > 1 &&
      (subs.length > 0 || this.fNode.GetSubChartFlag() === enumSubChartFlag.drilldown);
    return result;
  }

  TopConnectorPoint() {
    var connector = { x: 0, y: 0 };
    if (Array.isArray(this.fAnchorUp) && this.fAnchorUp.length > 0) {
      var xArray = [];
      for (var i = 0; i < this.fAnchorUp.length; i++) {
        xArray.push(this.fAnchorUp[i].x);
      }
      connector.x = this.GetMiddleX(xArray);
      connector.y = this.fAnchorUp[0].y;
    } else {
      connector = this.fAnchorUp;
    }

    return connector;
  }

  /**
   * @return {number}
   */
  YForSubs() {
    var Y = 0;
    if (this.fNodePoly) {
      Y = this.fNodePoly.fEnvelope.fBottom;
    }

    return Y;
  }

  /**
   * @return {number}
   */
  XforAssists() {
    var X = this.BottomAnchorPoint().x;
    if (this.NumChildren("AssistantLeft") > 0 || this.NumChildren("AssistantRight") > 0) {
      return X;
    }

    return X;
  }

  YforAssists() {
    var Y = this.fNodePoly.fEnvelope.fBottom;

    return Y;
  }

  UpdateAnchorPoint() {
    // This is done when branch style has changed.

    if (Array.isArray(this.fAnchorDown) && this.fAnchorDown.length > 1) {
      return;
    }

    var anchor = Array.isArray(this.fAnchorDown) ? this.fAnchorDown[0] : this.fAnchorDown;

    if (!anchor) {
      return;
    }

    switch (this.fSubBranchStyle) {
      case enumBranchStyle.onecolumnleft:
        anchor.x = this.fBox.fRight - ONE_COLUMN_OFFSET;
        break;

      case enumBranchStyle.onecolumnright:
        anchor.x = this.fBox.fLeft + ONE_COLUMN_OFFSET;
        break;

      case enumBranchStyle.onecolumncenterleft:
        anchor.x = this.fBox.fRight;
        break;

      case enumBranchStyle.onecolumncenterright:
        anchor.x = this.fBox.fLeft;
        break;
    }

    if (Array.isArray(this.fAnchorDown)) {
      this.fAnchorDown.splice(0, 1, anchor);
    } else {
      this.fAnchorDown = anchor;
    }
  }

  /**
   * @return {number}
   */
  GetFittedXForSubs(x, subs) {
    var fittedX = x;
    if (subs.length === 0) {
      return fittedX;
    }

    var center0 = subs[0].Center();
    if (subs.length === 1) {
      fittedX = center0.x;
      return fittedX;
    }

    if (x < center0.x + SNAP_CENTER_DISTANCE) {
      fittedX = center0.x + SNAP_CENTER_DISTANCE;
      return fittedX;
    }

    if (x > subs[subs.length - 1].Center().x - SNAP_CENTER_DISTANCE) {
      fittedX = subs[subs.length - 1].Center().x - SNAP_CENTER_DISTANCE;
      return fittedX;
    }

    if (subs.length > 2) {
      for (var i = 1; i < subs.length - 1; i++) {
        if (
          x > subs[i].Center().x - SNAP_CENTER_DISTANCE &&
          x < subs[i].Center().x + SNAP_CENTER_DISTANCE
        ) {
          fittedX = subs[i].Center().x;
          return fittedX;
        }
      }
    }

    return fittedX;
  }

  /**
   * @return {number}
   */
  GetMiddleXforTreeSubs(subs) {
    var bestX = 0;

    if (subs && subs.length > 0) {
      var xArray = [];
      for (var i = 0; i < subs.length; i++) {
        xArray.push(subs[i].TopConnectorPoint().x);
      }
      bestX = this.GetMiddleX(xArray);
    }

    return bestX;
  }

  GetMiddleX(xarray) {
    var midX = 0;
    var n = xarray.length;
    if (n === 0) {
      return midX;
    }

    if (n === 1) {
      midX = xarray[0];
    } else if (n === 2) {
      midX = Math.round((xarray[0] + xarray[1]) / 2);
    } else {
      midX = Math.round((xarray[0] + xarray[n - 1]) / 2);
      for (var i = 0; i < n; i++) {
        if (midX <= xarray[i]) {
          var leftX = xarray[i - 1];
          var rightX = xarray[i];
          var leftRightX = Math.round((leftX + rightX) / 2);
          break;
        }
      }

      if (midX - leftX < SNAP_CENTER_DISTANCE) {
        midX = leftX;
      } else if (rightX - midX < SNAP_CENTER_DISTANCE) {
        midX = rightX;
      } else if (Math.abs(midX - leftRightX) < SNAP_CENTER_DISTANCE) {
        midX = leftRightX;
      }
    }

    return midX;
  }

  /**
   * @return {number}
   */
  CalculateCorrectedChartHeight() {
    return (
      this.GetHeight() -
      2 * this.fBox.fBorderThickness -
      2 * Math.max(this.fBox.fMargins.fTop, this.fBox.fTopArrow.height)
    );
  }

  SetCorrectedHeight(height) {
    this.fCorrectedChartHeight = height;
    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      child.SetCorrectedHeight(height);
    }
  }

  /**
   * @return {number}
   */
  GetCorrectedHeight() {
    return this.fCorrectedChartHeight;
  }

  /**
   * @return {number}
   */
  GetCorrectedY(chartType) {
    var correctedY =
      chartType === enumChartTypes.inverted
        ? this.GetCorrectedHeight() -
          this.fBox.fTop -
          this.fBox.fHeight +
          this.fBox.fBorderThickness
        : this.fBox.fTop + this.fBox.fBorderThickness;

    return correctedY;
  }

  UpdatePositions(chartType, doRecursive) {
    var logUpdatePositions = false;
    if (this.fNode) {
      this.fCalculatedPosition.x = this.fBox.fLeft + this.fBox.fBorderThickness;
      this.fCalculatedPosition.y = this.GetCorrectedY(chartType);
      this.fNode.SetPosition(this.fCalculatedPosition.x, this.fCalculatedPosition.y);
      if (logUpdatePositions) {
        var XPosition = this.fCalculatedPosition.x;
        var YPosition = this.fCalculatedPosition.y;
        if (this.fNode.GetParent()) {
          console.log(
            this.fNode.GetID() + "[" + this.fNode.GetParent().GetID() + "] ",
            XPosition,
            YPosition
          );
        } else {
          if (this.fNode.GetContainerType() !== enumContainerType.nonmrb) {
            console.log(this.fNode.GetID() + "[*] ", XPosition, YPosition);
          } else {
            console.log(this.fNode.GetID() + "[ ] ", XPosition, YPosition);
          }
        }
      }
    }

    if (doRecursive) {
      var children = this.GetChildren();
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        child.UpdatePositions(chartType, doRecursive);
      }
    }
  }

  // returns two arrays of structures that contain NodePolys and information related to
  // their index.
  getSubPolys(nonSingles, singles) {
    nonSingles.length = 0;
    singles.length = 0;
    var nSubs = this.NumChildren("Subordinate");
    for (var i = 0; i < nSubs; i++) {
      var sub = this.GetEntity("Subordinate", i);
      var subPoly = sub.fNodePoly.Clone();
      if (sub.NumChildren() === 0) {
        singles.push({ poly: subPoly, index: i, single: true });
      } else {
        nonSingles.push({ poly: subPoly, index: i, single: false });
      }
    }
  }

  LogNodeEntity() {
    var LogMessage = "";

    LogMessage += this.fID + ": ";

    LogMessage +=
      "Box: [" +
      this.fBox.fLeft +
      " " +
      this.fBox.fRight +
      " " +
      this.fBox.fTop +
      " " +
      this.fBox.fBottom +
      "] ";
    LogMessage +=
      "Envelope: [" +
      this.fNodePoly.fEnvelope.fLeft +
      " " +
      this.fNodePoly.fEnvelope.fRight +
      " " +
      this.fNodePoly.fEnvelope.fTop +
      " " +
      this.fNodePoly.fEnvelope.fBottom +
      "]";
    LogMessage +=
      "Width/Height: [" +
      (this.fNodePoly.fEnvelope.fRight - this.fNodePoly.fEnvelope.fLeft) +
      " " +
      (this.fNodePoly.fEnvelope.fBottom - this.fNodePoly.fEnvelope.fTop) +
      "]";
    console.log(LogMessage);
  }

  LogFull(tabSpace) {
    var LogMessage = tabSpace;

    if (this.fNode == null || this.fNode.fDataObj == null) {
      var nodeName = "***";
    } else {
      nodeName = this.fNode.fDataObj.GetFieldValue(this.fNode.fDataObj.GetFieldDefByID("Name"));
    }
    LogMessage += nodeName + ", ";
    LogMessage +=
      this.fBox.fLeft + ", " + this.fBox.fTop + ", " + this.fBox.fRight + ", " + this.fBox.fBottom;
    console.log(LogMessage);
    tabSpace += tabSpace;
    var nSubs = this.NumChildren("Subordinate");
    if (nSubs > 0) {
      console.log(tabSpace + "Subordinates:");
      for (var i = 0; i < nSubs; i++) {
        var sub = this.GetEntity("Subordinate", i);
        sub.LogFull(tabSpace);
      }
    }
    nSubs = this.NumChildren("InsideChild");
    if (nSubs > 0) {
      console.log(tabSpace + "Inside Children:");
      for (i = 0; i < nSubs; i++) {
        sub = this.GetEntity("InsideChild", i);
        sub.LogFull(tabSpace);
      }
    }
  }

  signature(offset, factor) {
    if (typeof offset === "undefined") {
      offset = 0;
    }

    if (typeof factor === "undefined") {
      factor = 1.0;
    }

    var x = factor * (this.fBasePoly.fEnvelope.fWidth + this.fBasePoly.fEnvelope.fHeight + offset);

    var sg = {
      key1: this.NumChildren().toString(),
      key2: x
    };

    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      offset++;
      factor *= 1.02;
      var sgChild = child.signature(offset, factor);
      sg.key1 += sgChild.key1;
      sg.key2 += sgChild.key2;
    }

    return sg;
  }

  getTotalBoxAreas() {
    var area = this.fBox.fHeight * this.fBox.fWidth;

    var children = this.GetChildren();
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      area += child.getTotalBoxAreas();
    }
    return area;
  }

  GetBoundary(includeShadow, onlyBox, chartType, numNodes) {
    var delta = { x: 0, y: 0 };
    if (this.fNode) {
      delta.x = this.fNode.GetPositionX() - this.fCalculatedPosition.x;
      delta.y = this.fNode.GetPositionY() - this.fCalculatedPosition.y;
    }

    var shadowOffset = includeShadow ? 0 : -this.fShadow;
    if (onlyBox) {
      var yTopCorrection = 0;
      var yBottomCorrection = 0;
      if (chartType === enumChartTypes.inverted) {
        yTopCorrection =
          Math.max(this.fBox.fBottomArrow.height, this.fBox.fMargins.fBottom) -
          Math.max(this.fBox.fTopArrow.height, this.fBox.fMargins.fTop);
        if (numNodes === 1) {
          yBottomCorrection =
            Math.max(this.fBox.fBottomArrow.height, this.fBox.fMargins.fBottom) -
            Math.max(this.fBox.fTopArrow.height, this.fBox.fMargins.fTop);
        } else {
          yBottomCorrection =
            Math.max(this.fBox.fBottomArrow.height, this.fBox.fMargins.fBottom) -
            Math.max(this.fBox.fTopArrow.height, this.fBox.fMargins.fTop);
        }
      }
      /*
          var boundary = {
              left: this.fBox.fLeft + delta.x,
              right: this.fBox.fRight + delta.x + shadowOffset,
              top: this.fBox.fTop + delta.y + yTopCorrection,
              bottom: this.fBox.fBottom + delta.y + shadowOffset + yBottomCorrection
          };

           */
      var boundary = {
        left: this.fBox.fLeft + delta.x,
        right: this.fBox.fRight + delta.x + shadowOffset,
        top: this.fBox.fTop + delta.y + yTopCorrection,
        bottom: this.fBox.fBottom + delta.y + shadowOffset + yBottomCorrection
      };
    } else {
      boundary = {
        left: this.fNodePoly.fEnvelope.fLeft + delta.x,
        right: this.fNodePoly.fEnvelope.fRight + delta.x + shadowOffset,
        top: this.fNodePoly.fEnvelope.fTop + delta.y,
        bottom: this.fNodePoly.fEnvelope.fBottom + delta.y + shadowOffset
      };
    }

    // adjustment for width and height, if for some odd reason the box is not the same size!!
    //Note- this should eventually get removed.
    if (this.fNode) {
      var deltaW = this.fBox.fWidth - 2 * this.fBox.fBorderThickness - this.fNode.GetBoxWidth();
      boundary.right -= deltaW;
      var deltaH = this.fBox.fHeight - 2 * this.fBox.fBorderThickness - this.fNode.GetBoxHeight();
      boundary.bottom -= deltaH;
    }

    return boundary;
  }
}
